/**
 * PLUS+ULTRA x CADENCE INTERACTIVE SYSTEM v2.1
 * Pure White Theme Client-Side Cart, Dynamic Function Tabs, Category Filters,
 * Full 8-9 Slide PDP Secondary Gallery Carousel + Supplement Facts Quick-Jump,
 * Pack & Subscription Matrix, Accordions, Modals & Carousel Smooth Scrolling.
 */

(function () {
  'use strict';

  // Free US shipping minimum in dollars. 0 = every US order ships free (Shopify shipping settings, Oct 28, 2026).
  // The matching text lives in snippets/ppu-free-shipping-text.liquid; change both together.
  const FREE_SHIPPING_MIN = 0;
  const STORAGE_KEY = 'ppu_cadence_cart_v1';
  const CART_IMAGES_KEY = 'ppu_cadence_cart_images_v1';
  const SOLD_OUT_MESSAGE = 'Sorry, this product is sold out';
  const REFUSED_CODE_MESSAGE = "Your welcome code can't be used on this order; the bottle discount still applies.";

  // Shopify's discount_code cookie holds WELCOME20: set by a /discount/WELCOME20 link (welcome email, ads).
  // Only WELCOME20 counts, never STACK10 or other codes.
  function welcomeCookie() {
    const m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!m) return false;
    let code = m[1];
    try { code = decodeURIComponent(code); } catch (e) {}
    return code.trim().toUpperCase() === 'WELCOME20';
  }
  // The shopper carries the WELCOME20 code: the cookie above, or the code saved after a sign-up in this browser.
  // Used by the pop-up rule (alreadyRegistered).
  function carriesWelcomeCode() {
    try {
      if ((localStorage.getItem('ppu_active_discount_code') || '').trim().toUpperCase() === 'WELCOME20') return true;
    } catch (e) {}
    return welcomeCookie();
  }
  // S-44: the product page starts on One-Time Purchase only for the cookie, or a sign-up confirmed in this browser
  // session. The stored code alone does not count: it stays after a finished order and WELCOME20 is one use only.
  const WELCOME_SESSION_KEY = 'ppu_welcome_session_v1';
  function welcomeSwitchWanted() {
    try { if (sessionStorage.getItem(WELCOME_SESSION_KEY) === '1') return true; } catch (e) {}
    return welcomeCookie();
  }
  // Set by initPDP on product pages; called again after a sign-up on the same page view.
  let applyWelcomeOneTime = null;
  // Plain light-grey square shown when a bag thumbnail fails to load (replaces a dead placeholder site)
  window.PPU_THUMB_FALLBACK = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22%3E%3Crect width=%2280%22 height=%2280%22 fill=%22%23F2F2EF%22/%3E%3C/svg%3E';

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
    ));
  }

  // Variant IDs Shopify reports as unavailable (printed by snippets/ppu-cart-and-modals.liquid)
  function isVariantSoldOut(variantId) {
    const list = window.PPU_SOLD_OUT_VARIANTS || [];
    return Boolean(variantId) && list.indexOf(String(variantId)) > -1;
  }

  // Safety net for add buttons whose markup has no Liquid sold-out check (bundles, other templates)
  function markSoldOutButtons() {
    document.querySelectorAll('[data-add-to-cart]:not(.js-pdp-add-btn)').forEach((btn) => {
      const ids = (btn.dataset.variantIds || btn.dataset.variantId || '').split(',').filter(Boolean);
      if (!ids.some(isVariantSoldOut)) return;
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.dataset.soldOut = 'true';
      btn.textContent = 'SOLD OUT';
      btn.style.opacity = '0.55';
      btn.style.cursor = 'not-allowed';
    });
  }

  // =========================================================================
  // 1. CART STATE & DRAWER MANAGEMENT
  // On the live store the Shopify cart (/cart.js) is the source of truth; the
  // localStorage copy is only a cache for the first paint and for local previews.
  // =========================================================================
  // S-31: the bag shows the same short names as the product pages (Shopify's product titles are longer).
  const PPU_SHORT_NAMES = {
    'glp-1-support-capsules': 'GLPLUS+ 10-IN-1',
    'magplus-7-in-1-sleep-formula': 'MAGPLUS+ 7-IN-1 SLEEP',
    'totalmag-13-in-1-magnesium-complex': 'MAGPLUS+ 14-IN-1',
    'zeolite-detox-complex': 'ZEOLITE+ 8-IN-1',
    'soursop-bitters': 'SOURSOP BITTERS+ 16-IN-1',
    'glp-1-synbiotic': 'GLPLUS+ 20-IN-1 SYNBIOTIC',
    'glp-1-gummies': 'GLPLUS+ 8-IN-1 GUMMIES',
    'saffron': 'SAFFRON+ 9-IN-1',
    'glo-liposomal-glutathione': 'GLOPLUS+ 11-IN-1',
    'beef-organs-for-women': 'BEEF ORGANS+ 11-IN-1',
    'viva-la-v-feminine-balance-gummies': 'SLIPPERY ELM+ 8-IN-1',
    'testplus-testosterone-support': 'TESTPLUS+ 8-IN-1',
    'shilajit-resin-complex': 'SHILAJITPLUS+ 5-IN-1',
    'maca-complex': 'MACAPLUS+ 3-IN-1'
  };

  const PPUCart = {
    items: [],
    totals: null,

    isLive() {
      return Boolean(window.Shopify);
    },

    init() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          this.items = JSON.parse(stored);
        }
      } catch (e) {
        console.warn('Could not read cart from localStorage', e);
        this.items = [];
      }
      // Retired stack codes: drop any stored copy so checkout never sends a dead code
      try { const oldCode = localStorage.getItem('ppu_active_discount_code'); if (oldCode && /STACK10$/i.test(oldCode.trim())) localStorage.removeItem('ppu_active_discount_code'); } catch (e) {}
      this.bindEvents();
      this.render();
      if (this.isLive()) {
        this.refresh();
        // Pages restored from the back/forward cache keep old state, so re-read the real cart
        window.addEventListener('pageshow', (e) => {
          if (e.persisted) this.refresh();
        });
      }
    },

    save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        console.warn('Could not save cart to localStorage', e);
      }
      this.render();
    },

    cartRequest(url, body) {
      const opts = body
        ? { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
        : { credentials: 'same-origin', headers: { Accept: 'application/json' } };
      return fetch(url, opts).then((res) => res.json().catch(() => ({})).then((data) => {
        if (!res.ok) {
          const err = new Error((data && (data.description || data.message)) || `Cart request failed (${res.status})`);
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      }));
    },

    // Serialize cart changes so rapid +/- clicks apply in order against fresh quantities
    queue(task) {
      this._queue = (this._queue || Promise.resolve()).then(task).catch((e) => {
        console.warn('Cart update failed', e);
        this.showToast('Sorry, we could not update your bag. Please try again.', true);
      });
      return this._queue;
    },

    refresh() {
      return this.cartRequest('/cart.js')
        .then((cart) => {
          this.loadFailed = false;
          this.applyShopifyCart(cart);
          return cart;
        })
        .catch((e) => {
          console.warn('Could not load the Shopify cart', e);
          // The saved copy may be out of date: say so and keep Checkout off until the real bag loads
          this.loadFailed = true;
          this.render();
          return null;
        });
    },

    loadImageMap() {
      try {
        return JSON.parse(localStorage.getItem(CART_IMAGES_KEY) || '{}') || {};
      } catch (e) {
        return {};
      }
    },

    rememberImage(variantIds, image) {
      if (!image) return;
      const map = this.loadImageMap();
      variantIds.filter(Boolean).forEach((v) => { map[String(v)] = image; });
      try {
        localStorage.setItem(CART_IMAGES_KEY, JSON.stringify(map));
      } catch (e) {}
    },

    applyShopifyCart(cart) {
      const images = this.loadImageMap();
      this.items = (cart.items || []).map((line) => {
        const plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
        const planName = plan ? plan.name : 'One-Time Purchase';
        const variantLabel = line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '';
        // S-28: on a subscription line Shopify prices every money field at the plan price, so the one-time price of
        // the same bottles comes from selling_plan_allocation.compare_at_price (cents per bottle) for the struck-out saving.
        const alloc = line.selling_plan_allocation;
        const compareUnit = alloc && typeof alloc.compare_at_price === 'number' && alloc.compare_at_price > alloc.price ? alloc.compare_at_price : null;
        return {
          key: line.key,
          id: String(line.product_id),
          variantId: String(line.variant_id),
          sellingPlan: plan ? String(plan.id) : '',
          title: PPU_SHORT_NAMES[line.handle] || line.product_title || line.title,
          image: images[String(line.variant_id)] || line.image || '',
          price: line.final_price / 100,
          linePrice: line.final_line_price / 100,
          originalLinePrice: line.original_line_price / 100,
          compareLinePrice: compareUnit ? (compareUnit * line.quantity) / 100 : null,
          plan: variantLabel ? `${variantLabel} · ${planName}` : planName,
          qty: line.quantity,
          handle: line.handle || ''
        };
      });
      this.totals = {
        count: cart.item_count || 0,
        total: (cart.total_price || 0) / 100,
        discount: (cart.total_discount || 0) / 100,
        discountTitles: this.getDiscountTitles(cart),
        // S-41: the codes Shopify holds for this cart (applicable: false = Shopify refused it on this order),
        // and the code discounts it applied to the whole order (they replace the Buy 3 / Buy 6 bottle discount)
        codes: (cart.discount_codes || []).map((c) => ({ code: String((c && c.code) || '').trim().toUpperCase(), applicable: c && c.applicable })),
        codeTitles: (cart.cart_level_discount_applications || [])
          .filter((app) => app && app.type === 'discount_code' && Number(app.total_allocated_amount) > 0)
          .map((app) => ({ title: String(app.title || '').trim(), percent: app.value_type === 'percentage' ? Math.round(parseFloat(app.value)) : null }))
      };
      this.save();
    },

    // Titles of the discounts Shopify actually applied to this cart (automatic discounts and codes), for the badge
    getDiscountTitles(cart) {
      const titles = [];
      const addTitle = (application, amount) => {
        const title = application && String(application.title || '').trim();
        if (!title || Number(amount) <= 0 || titles.indexOf(title) > -1) return;
        titles.push(title);
      };
      (cart.cart_level_discount_applications || []).forEach((app) => addTitle(app, app.total_allocated_amount));
      (cart.items || []).forEach((line) => {
        (line.line_level_discount_allocations || []).concat(line.discount_allocations || []).forEach((alloc) => {
          addTitle(alloc && alloc.discount_application, alloc && alloc.amount);
        });
      });
      return titles;
    },

    // S-41: true when Shopify holds this code on the cart and reports it as not usable (applicable: false)
    storedCodeRefused(code) {
      const codes = (this.totals && this.totals.codes) || [];
      return codes.some((c) => c.code === code && c.applicable === false);
    },

    // S-41: the Buy 3 / Buy 6 bottle discount a code on the whole order replaced on this line, or null.
    // Only a one-time line of 3+ bottles with no line discount left (full price) while a code discount is applied.
    replacedPackTier(item) {
      const codeTitles = (this.totals && this.totals.codeTitles) || [];
      if (!codeTitles.length || !item || item.sellingPlan || (item.plan && /subscribe/i.test(item.plan))) return null;
      if (!(item.qty >= 3) || typeof item.originalLinePrice !== 'number' || typeof item.linePrice !== 'number') return null;
      if (Math.abs(item.originalLinePrice - item.linePrice) > 0.004) return null;
      const codePercent = codeTitles[0].percent;
      return item.qty >= 6
        ? { title: 'Buy 6 bottles, save 15%', percent: 15, codePercent }
        : { title: 'Buy 3 bottles, save 10%', percent: 10, codePercent };
    },

    changeLine(item, quantity, successMessage) {
      const idx = this.items.indexOf(item);
      const body = item.key ? { id: item.key, quantity } : { line: idx + 1, quantity };
      return this.cartRequest('/cart/change.js', body)
        .then((cart) => {
          this.applyShopifyCart(cart);
          if (successMessage) this.showToast(successMessage);
        })
        .catch((err) => {
          this.showToast(err.message || 'Sorry, we could not update your bag. Please try again.', true);
          return this.refresh();
        });
    },

    findLine(key, idx) {
      return key ? this.items.find((i) => i.key === key) : this.items[idx];
    },

    isSoldOutError(err) {
      const text = `${(err && err.message) || ''} ${(err && err.data && err.data.message) || ''}`;
      return /sold out|out of stock|can't add more|can only add/i.test(text);
    },

    addErrorMessage(err) {
      if (err && err.status === 422 && /sold out|out of stock/i.test(err.message || '')) {
        return SOLD_OUT_MESSAGE;
      }
      return (err && err.status && err.message) || 'Sorry, we could not add this item. Please try again.';
    },

    setButtonBusy(btn, busy) {
      if (!btn || btn.dataset.soldOut === 'true') return;
      btn.disabled = busy;
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('aria-busy');
      }
    },

    addItem(item) {
      const existingIdx = this.items.findIndex(
        (i) => i.id === item.id && i.plan === item.plan
      );
      if (existingIdx > -1) {
        this.items[existingIdx].qty += item.qty || 1;
        if (item.sellingPlan) this.items[existingIdx].sellingPlan = item.sellingPlan;
      } else {
        this.items.push({
          id: item.id,
          variantId: item.variantId || '',
          sellingPlan: item.sellingPlan || '',
          title: item.title,
          image: item.image,
          price: parseFloat(item.price) || 27.99,
          plan: item.plan || 'One-Time Purchase',
          qty: item.qty || 1,
          handle: item.handle || ''
        });
      }
      this.save();
      this.showToast(`Added ${item.title} to your bag`);
      this.open();
    },

    removeItem(idx) {
      if (idx < 0 || idx >= this.items.length) return;
      if (this.isLive()) {
        const key = this.items[idx].key;
        this.queue(() => {
          const line = this.findLine(key, idx);
          return line ? this.changeLine(line, 0, `Removed ${line.title}`) : null;
        });
        return;
      }
      const removed = this.items.splice(idx, 1);
      this.save();
      if (removed.length) {
        this.showToast(`Removed ${removed[0].title}`);
      }
    },

    updateQty(idx, delta) {
      if (idx < 0 || idx >= this.items.length) return;
      if (this.isLive()) {
        const key = this.items[idx].key;
        this.queue(() => {
          const line = this.findLine(key, idx);
          if (!line) return null;
          const nextQty = Math.max(0, line.qty + delta);
          return this.changeLine(line, nextQty, nextQty === 0 ? `Removed ${line.title}` : '');
        });
        return;
      }
      this.items[idx].qty += delta;
      if (this.items[idx].qty <= 0) {
        this.removeItem(idx);
      } else {
        this.save();
      }
    },

    getTotalCount() {
      if (this.totals) return this.totals.count;
      return this.items.reduce((sum, i) => sum + i.qty, 0);
    },

    // Live store: Shopify's total_price (after automatic and code discounts)
    getSubtotal() {
      if (this.totals) return this.totals.total;
      return this.items.reduce((sum, i) => sum + this.getLinePrice(i), 0);
    },

    getLinePrice(item) {
      return typeof item.linePrice === 'number' ? item.linePrice : item.price * item.qty;
    },

    open() {
      const drawer = document.getElementById('cartDrawer');
      const overlay = document.getElementById('cartOverlay');
      if (drawer && overlay) {
        drawer.classList.add('active');
        overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    },

    close() {
      const drawer = document.getElementById('cartDrawer');
      const overlay = document.getElementById('cartOverlay');
      if (drawer && overlay) {
        drawer.classList.remove('active');
        overlay.classList.remove('active');
        document.body.style.overflow = '';
      }
    },

    toggle() {
      const drawer = document.getElementById('cartDrawer');
      if (drawer && drawer.classList.contains('active')) {
        this.close();
      } else {
        this.open();
      }
    },

    render() {
      const totalCount = this.getTotalCount();
      const subtotal = this.getSubtotal();

      // Update badge counts everywhere
      document.querySelectorAll('.js-cart-count').forEach((el) => {
        el.textContent = totalCount;
      });

      // Free shipping bar: shows the text from snippets/ppu-free-shipping-text.liquid, and shows an amount
      // still needed (with the progress bar) only when FREE_SHIPPING_MIN is above 0
      const meterFill = document.querySelector('.js-shipping-fill');
      const meterText = document.querySelector('.js-shipping-text');
      if (meterFill && meterText) {
        if (meterText.dataset.freeShippingHtml === undefined) {
          meterText.dataset.freeShippingHtml = meterText.innerHTML;
        }
        const remaining = Math.max(0, FREE_SHIPPING_MIN - subtotal);
        const meterTrack = meterFill.parentElement;
        if (meterTrack) meterTrack.hidden = remaining <= 0;

        if (remaining > 0) {
          const percent = Math.min(100, (subtotal / FREE_SHIPPING_MIN) * 100);
          meterFill.style.width = `${percent}%`;
          meterText.innerHTML = `<span>Add <strong>$${remaining.toFixed(2)}</strong> for free US shipping</span><span>${Math.round(percent)}%</span>`;
        } else {
          meterText.innerHTML = meterText.dataset.freeShippingHtml;
        }
      }

      // Render items list
      const listEl = document.querySelector('.js-cart-items');
      const subtotalEl = document.querySelector('.js-cart-subtotal');
      const checkoutBtn = document.querySelector('.js-checkout-btn');

      if (subtotalEl) {
        subtotalEl.textContent = `$${subtotal.toFixed(2)}`;
      }

      // Savings Shopify applied (automatic bundle/volume discounts and codes)
      const savingsRow = document.querySelector('.js-cart-savings-row');
      const savingsEl = document.querySelector('.js-cart-savings');
      const discount = this.totals ? this.totals.discount : 0;
      if (savingsRow && savingsEl) {
        savingsRow.style.display = this.items.length > 0 && discount > 0.004 ? '' : 'none';
        savingsEl.textContent = `-$${discount.toFixed(2)}`;
      }

      // Single-Discount-Code & Subscription Non-Stacking Guard (the stored code is only sent at checkout)
      const isSubItem = (i) => Boolean(i.sellingPlan) || (i.plan && /subscribe/i.test(i.plan));
      const allSubItems = this.items.length > 0 && this.items.every(isSubItem);
      const rawCode = (typeof localStorage !== 'undefined') ? localStorage.getItem('ppu_active_discount_code') : null;
      const activeCode = rawCode ? rawCode.split(',')[0].trim().toUpperCase() : null;
      if (allSubItems && activeCode) {
        try { localStorage.removeItem('ppu_active_discount_code'); } catch (e) {}
      } else if (activeCode && this.storedCodeRefused(activeCode)) {
        // S-41: Shopify refused the stored code on this cart, so it is never sent to checkout again
        try { localStorage.removeItem('ppu_active_discount_code'); } catch (e) {}
        this.showToast(REFUSED_CODE_MESSAGE, true);
      } else if (activeCode && activeCode !== rawCode) {
        try { localStorage.setItem('ppu_active_discount_code', activeCode); } catch (e) {}
      }

      // Discount badge: only discounts Shopify reports on the cart, named by their Shopify titles.
      // An all-subscription bag also keeps its line saying codes do not stack (true: the guard above drops the code).
      const discountTitles = (this.totals && this.totals.discountTitles) || [];
      const badgeLines = discountTitles.map((title) => `✓ ${title} applied`);
      // S-41: a code on the whole order and the Buy 3 / Buy 6 bottle discount do not combine; Shopify keeps one.
      // When the code won, name the bottle discount it replaced (the line then shows its full price).
      const replacedTitles = [];
      this.items.forEach((item) => {
        const tier = this.replacedPackTier(item);
        if (tier && replacedTitles.indexOf(tier.title) === -1) replacedTitles.push(tier.title);
      });
      replacedTitles.forEach((title) => badgeLines.push(`replaces ${title}`));
      if (allSubItems) badgeLines.unshift('✓ SUBSCRIBE & SAVE ACTIVE · Cannot stack with promo codes');
      let badgeEl = document.getElementById('cartDiscountBadge');
      if (this.items.length > 0 && badgeLines.length) {
        if (!badgeEl) {
          badgeEl = document.createElement('div');
          badgeEl.id = 'cartDiscountBadge';
          badgeEl.style.cssText = 'background:#E8F5E9; color:#2E7D32; font-size:11.5px; font-weight:600; padding:6px 10px; border-radius:4px; margin-bottom:8px; text-align:center; border:1px solid #C8E6C9; letter-spacing:0.02em;';
          const subtotalRow = document.querySelector('.cart-subtotal-row');
          if (subtotalRow && subtotalRow.parentNode) {
            subtotalRow.parentNode.insertBefore(badgeEl, subtotalRow);
          }
        }
        badgeEl.textContent = badgeLines.join(' · ');
        badgeEl.style.display = 'block';
      } else if (badgeEl) {
        badgeEl.style.display = 'none';
      }

      if (checkoutBtn) {
        const checkoutOff = this.items.length === 0 || Boolean(this.loadFailed);
        checkoutBtn.disabled = checkoutOff;
        checkoutBtn.setAttribute('aria-disabled', checkoutOff ? 'true' : 'false');
        if (checkoutOff) {
          checkoutBtn.style.opacity = '0.5';
          checkoutBtn.style.pointerEvents = 'none';
        } else {
          checkoutBtn.style.opacity = '1';
          checkoutBtn.style.pointerEvents = 'auto';
        }
      }

      if (!listEl) return;

      const loadErrorHtml = this.loadFailed
        ? `<div class="cart-load-error" role="alert" style="background:#FFF4E5; color:#7A4100; border:1px solid #F5C99B; border-radius:6px; padding:10px 12px; margin-bottom:10px; font-size:13px; font-weight:600;">We could not load your bag. <button type="button" class="js-cart-retry" style="background:none; border:0; padding:0; font:inherit; color:inherit; text-decoration:underline; cursor:pointer;">Tap to retry</button></div>`
        : '';

      if (this.items.length === 0 && !this.loadFailed) {
        listEl.innerHTML = `
          <div class="cart-empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.4;">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <path d="M16 10a4 4 0 0 1-8 0"></path>
            </svg>
            <p style="font-size: 14px; font-weight: 500; color: var(--ink-muted);">Your protocol bag is empty</p>
            <a href="${this.getShopPath()}" class="cadence-tab-pill js-close-cart" style="text-decoration:none; margin-top: 10px;">Explore the Range</a>
          </div>
        `;
        return;
      }

      listEl.innerHTML = loadErrorHtml + this.items
        .map((item, idx) => {
          const linePrice = this.getLinePrice(item);
          const originalPrice = typeof item.originalLinePrice === 'number' ? item.originalLinePrice : linePrice;
          // A subscription line is one plan: changing the count here would put extra bottles on that plan's price
          // (for example a 2nd bottle at 40%), so +/- is hidden on every line with a selling plan. Remove stays.
          const lockedQty = Boolean(item.sellingPlan);
          // S-28: a subscription line strikes out the one-time price of the same bottles (compareLinePrice);
          // a one-time line keeps Shopify's own before-discount price.
          const strikePrice = typeof item.compareLinePrice === 'number' && item.compareLinePrice > linePrice + 0.004 ? item.compareLinePrice : originalPrice;
          const priceHtml = strikePrice > linePrice + 0.004
            ? `<s style="opacity:0.5; font-weight:500; margin-right:4px;">$${strikePrice.toFixed(2)}</s>$${linePrice.toFixed(2)}`
            : `$${linePrice.toFixed(2)}`;
          const tier = this.replacedPackTier(item);
          const noteHtml = tier
            ? `<div class="cart-item-note" style="font-size:13px; font-weight:500; line-height:1.3; color:var(--ink-muted); text-align:right; max-width:140px;">${tier.percent}% pack discount replaced by your ${tier.codePercent ? `${tier.codePercent}% ` : ''}code</div>`
            : '';
          return `
            <div class="cart-item-row" data-index="${idx}">
              <div class="cart-item-thumb cart-item__img">
                <img src="${escapeHtml(this.thumbFor(item.image))}" width="80" height="80" loading="lazy" alt="${escapeHtml(item.title)}" onerror="this.onerror=null; this.src=PPU_THUMB_FALLBACK">
              </div>
              <div class="cart-item-meta">
                <div class="cart-item-title">${escapeHtml(item.title)}</div>
                <div class="cart-item-plan">${escapeHtml(item.plan)}</div>
                <div class="cart-item-qty-row">
                  ${lockedQty ? '' : `<button type="button" class="cart-qty-btn js-qty-minus" data-index="${idx}" aria-label="Remove one ${escapeHtml(item.title)}">-</button>`}
                  <span class="cart-qty-val">${item.qty}</span>
                  ${lockedQty ? '' : `<button type="button" class="cart-qty-btn js-qty-plus" data-index="${idx}" aria-label="Add one more ${escapeHtml(item.title)}">+</button>`}
                </div>
              </div>
              <div class="cart-item-price-side">
                <div class="cart-item-price">${priceHtml}</div>
                ${noteHtml}
                <button type="button" class="cart-item-remove js-cart-remove" data-index="${idx}" aria-label="Remove ${escapeHtml(item.title)} from your bag">✕</button>
              </div>
            </div>
          `;
        })
        .join('');
    },

    // Bag thumbnail: the small copy of a bottle image when layout/theme.liquid lists one (PPU_ASSET_THUMB_MAP,
    // matched by file name so the original, -600 and -240 copies all find it), otherwise the image itself
    thumbFor(url) {
      const thumbs = window.PPU_ASSET_THUMB_MAP;
      if (!url || !thumbs) return url;
      const fileOf = (u) => String(u || '').split('?')[0].split('/').pop().replace(/-(600|240)(\.png)$/i, '$2').replace(/_\d+x\d*(\.png)$/i, '$1');
      const want = fileOf(url);
      const key = Object.keys(thumbs).find((k) => fileOf(k) === want);
      return (key && thumbs[key]) || url;
    },

    getShopPath() {
      if (window.Shopify || window.PPU_ASSET_MAP) {
        return '/collections/all';
      }
      if (window.location.pathname.includes('/products/') || window.location.pathname.includes('/collections/')) {
        return '../collections/shop.html';
      }
      return 'collections/shop.html';
    },

    showToast(message, isError) {
      let toast = document.getElementById('ppuToast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'ppuToast';
        toast.className = 'toast-notice';
        document.body.appendChild(toast);
      }
      toast.setAttribute('role', isError ? 'alert' : 'status');
      toast.innerHTML = isError
        ? `<span style="color:#FF8A80;">✕</span> <span>${escapeHtml(message)}</span>`
        : `<span style="color:#85E396;">✓</span> <span>${escapeHtml(message)}</span>`;
      toast.classList.add('active');
      clearTimeout(this._toastTimeout);
      this._toastTimeout = setTimeout(() => {
        toast.classList.remove('active');
      }, 3000);
    },

    bindEvents() {
      document.addEventListener('click', (e) => {
        // Open cart
        if (e.target.closest('.js-open-cart')) {
          e.preventDefault();
          this.open();
          return;
        }

        // Close cart
        if (e.target.closest('.js-close-cart') || e.target.id === 'cartOverlay') {
          e.preventDefault();
          this.close();
          return;
        }

        // Retry loading the bag after a failed /cart.js
        if (e.target.closest('.js-cart-retry')) {
          e.preventDefault();
          this.refresh();
          return;
        }

        // Qty plus
        const plusBtn = e.target.closest('.js-qty-plus');
        if (plusBtn) {
          e.preventDefault();
          const idx = parseInt(plusBtn.dataset.index, 10);
          this.updateQty(idx, 1);
          return;
        }

        // Qty minus
        const minusBtn = e.target.closest('.js-qty-minus');
        if (minusBtn) {
          e.preventDefault();
          const idx = parseInt(minusBtn.dataset.index, 10);
          this.updateQty(idx, -1);
          return;
        }

        // Remove item
        const removeBtn = e.target.closest('.js-cart-remove');
        if (removeBtn) {
          e.preventDefault();
          const idx = parseInt(removeBtn.dataset.index, 10);
          this.removeItem(idx);
          return;
        }

        // Add to cart buttons
        const addBtn = e.target.closest('[data-add-to-cart]');
        if (addBtn) {
          e.preventDefault();
          if (addBtn.getAttribute('aria-busy') === 'true') return;
          if (addBtn.dataset.soldOut === 'true') {
            this.showToast(SOLD_OUT_MESSAGE, true);
            return;
          }
          const rawDiscountCode = addBtn.getAttribute('data-discount-code') || addBtn.dataset.discountCode || '';
          const discountCode = rawDiscountCode ? rawDiscountCode.split(',')[0].trim().toUpperCase() : '';
          const id = addBtn.dataset.productId || Date.now().toString();
          const variantId = addBtn.dataset.variantId || '';
          const variantId1 = addBtn.dataset.variantId1 || variantId;
          const variantIds = addBtn.dataset.variantIds || '';
          const sellingPlan = addBtn.dataset.sellingPlan || '';
          const title = addBtn.dataset.productTitle || 'Formula';
          const image = addBtn.dataset.productImage || '';
          const price = parseFloat(addBtn.dataset.productPrice || '27.99');
          const plan = addBtn.dataset.productPlan || 'One-Time Purchase';
          const qty = parseInt(addBtn.dataset.productQty || '1', 10);
          const handle = addBtn.dataset.productHandle || '';
          const isSubscriptionAdd = Boolean(sellingPlan) || /subscribe/i.test(plan);

          // Enforce Single Discount Code & No Subscription Promo Stacking
          if (discountCode && !isSubscriptionAdd) {
            try {
              localStorage.setItem('ppu_active_discount_code', discountCode);
            } catch (err) {}
            fetch('/discount/' + encodeURIComponent(discountCode), { credentials: 'same-origin' }).catch(() => {});
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ attributes: { bundle_discount_code: discountCode, discount_stacking: 'single_code_only' } })
            }).catch(() => {});
          } else if (isSubscriptionAdd && this.items.every(i => Boolean(i.sellingPlan) || (i.plan && /subscribe/i.test(i.plan)))) {
            try {
              localStorage.removeItem('ppu_active_discount_code');
            } catch (err) {}
          }

          // Live Shopify storefront: add through native /cart/add.js, then re-render the drawer from /cart.js.
          // A refused add shows Shopify's error; it is never retried as a one-time purchase.
          if (this.isLive() && (variantIds || variantId)) {
            const itemProps = (discountCode && !isSubscriptionAdd) ? { 'Promo Code': discountCode } : {};
            const payload = variantIds
              ? { items: variantIds.split(',').filter(Boolean).map(v => ({ id: parseInt(v, 10), quantity: qty, properties: itemProps })) }
              : { id: parseInt(variantId, 10), quantity: qty, properties: itemProps };
            if (!variantIds && sellingPlan) payload.selling_plan = parseInt(sellingPlan, 10);
            this.setButtonBusy(addBtn, true);
            // Adds run in the same queue as +/- and Remove, so a quick add never races a quantity change
            this.queue(() => this.cartRequest('/cart/add.js', payload)
              .then(() => {
                if (!variantIds) this.rememberImage([variantId, variantId1], image);
                return this.refresh();
              })
              .then(() => {
                this.showToast(`Added ${title} to your bag`);
                this.open();
              })
              .catch((err) => {
                this.showToast(this.addErrorMessage(err), true);
                return this.refresh();
              })
              .then(() => this.setButtonBusy(addBtn, false)));
            return;
          }

          this.addItem({ id, variantId, sellingPlan, title, image, price, plan, qty: 1, handle });
          return;
        }

        // Checkout button action (routes to native /checkout on Shopify with single-code & subscription non-stacking guard)
        const checkoutBtn = e.target.closest('.js-checkout-btn, .cart-checkout-btn');
        if (checkoutBtn) {
          e.preventDefault();
          const allSubItems = this.items.length > 0 && this.items.every(i => Boolean(i.sellingPlan) || (i.plan && /subscribe/i.test(i.plan)));
          const rawCode = (typeof localStorage !== 'undefined') ? localStorage.getItem('ppu_active_discount_code') : null;
          const storedCode = rawCode ? rawCode.split(',')[0].trim().toUpperCase() : null;
          // S-41: never send a code Shopify refused on this cart; a code the cart already carries goes to checkout on its own
          const refused = Boolean(storedCode) && this.storedCodeRefused(storedCode);
          if (refused) {
            try { localStorage.removeItem('ppu_active_discount_code'); } catch (e) {}
          }
          const onCart = Boolean(storedCode) && ((this.totals && this.totals.codes) || []).some((c) => c.code === storedCode && c.applicable === true);
          const activeCode = (!allSubItems && storedCode && !refused && !onCart) ? storedCode : null;
          const checkoutUrl = activeCode ? `/checkout?discount=${encodeURIComponent(activeCode)}` : '/checkout';
          if (window.Shopify) {
            window.location.href = checkoutUrl;
            return;
          }
          const subtotal = this.getSubtotal().toFixed(2);
          const codeMsg = activeCode ? ` · Code Applied: ${activeCode}` : (allSubItems ? ' · Subscribe & Save Applied' : '');
          this.showToast(`Proceeding to Secure Checkout · Subtotal: $${subtotal}${codeMsg}`);
          return;
        }
      });
    }
  };

  // =========================================================================
  // 2. CATEGORY FILTER TABS
  // =========================================================================
  function initCategoryFilters() {
    const tabs = document.querySelectorAll('[data-filter]');
    const cards = document.querySelectorAll('[data-category]');

    if (!tabs.length || !cards.length) return;

    function applyFilter(filterValue) {
      tabs.forEach((t) => {
        t.classList.toggle('active', t.getAttribute('data-filter') === filterValue);
      });
      let shown = 0;
      cards.forEach((card) => {
        const categories = (card.getAttribute('data-category') || '').split(' ');
        const isBundle = card.classList.contains('bundle-card');
        const matches = (filterValue === 'all')
          ? !isBundle
          : (filterValue === 'bundles' ? isBundle : (!isBundle && categories.includes(filterValue)));

        if (matches && card.closest('#catalogProductGrid')) shown += 1;

        if (matches) {
          card.style.display = '';
          setTimeout(() => {
            card.style.opacity = '1';
            card.style.transform = 'translateY(0)';
          }, 10);
        } else {
          card.style.opacity = '0';
          card.style.transform = 'translateY(8px)';
          setTimeout(() => {
            card.style.display = 'none';
          }, 200);
        }
      });

      const countEl = document.querySelector('[data-catalog-count]');
      if (countEl) countEl.textContent = `Showing ${shown} Formulas & Systems`;
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        applyFilter(tab.getAttribute('data-filter'));
      });
    });

    // Honor ?cat=<slug> on collections/shop.html
    try {
      const params = new URLSearchParams(window.location.search);
      const catParam = params.get('cat');
      if (catParam) {
        const matchingTab = Array.from(tabs).find((t) => t.getAttribute('data-filter') === catParam);
        if (matchingTab) {
          applyFilter(catParam);
        }
      }
    } catch (e) {
      // ignore URLSearchParams errors
    }
  }

  // =========================================================================
  // 3. SHOP BY FUNCTION INTERACTIVE TABS & ACCORDIONS
  // =========================================================================
  const FUNCTION_DATA = {
    sleep: {
      title: 'MAGPLUS+ 7-IN-1 SLEEP',
      subtitle: 'FALL ASLEEP FASTER & WAKE UP REFRESHED',
      points: [
        'Absorbs quickly to help quiet a racing mind before bed',
        'Supports deep, uninterrupted nighttime sleep with zero morning grogginess',
        'Helps relax tense muscles and calm evening stress naturally'
      ],
      actives: [
        { val: '1,050mg', label: 'Liposomal Mag Complex' },
        { val: '200mg', label: 'L-Theanine' },
        { val: '50mg', label: 'Apigenin' },
        { val: '200mg', label: 'Chamomile & Valerian Root' }
      ],
      when: ['30-45 MIN BEFORE SLEEP', 'WITH OR WITHOUT FOOD', 'DAILY CONSISTENCY'],
      img: 'cdn/shop/files/editorial/cadence-fn-sleep.jpg?v=6',
      showcaseTitle: '<strong>MAGPLUS+</strong> 7-IN-1 SLEEP',
      showcaseSub: '7-in-1 Liposomal Sleep Complex',
      showcasePrice: '$34.99 • 60 Capsules',
      showcaseLink: '/products/magplus-7-in-1-sleep-formula',
      badge: 'Sleep'
    },
    weight: {
      title: 'GLPLUS+ 10-IN-1',
      subtitle: 'NATURAL CRAVING CONTROL & HEALTHY METABOLISM',
      points: [
        'Supports your body’s natural fullness signals so you feel satisfied longer',
        'Helps curb afternoon and late-night sugar cravings naturally',
        'Supports steady blood sugar and healthy digestion after meals'
      ],
      actives: [
        { val: '1,500mg', label: 'GLP-1 Support Blend' },
        { val: '95% + 70%', label: 'Berberine HCl & Beta-Glucan' },
        { val: '1B CFU', label: '5-Strain Synbiotics' },
        { val: '5mg', label: 'Zinc Citrate' }
      ],
      when: ['20 MIN BEFORE LARGEST MEAL', 'WITH 8OZ WATER', 'DAILY CONSISTENCY'],
      img: 'cdn/shop/files/editorial/cadence-fn-weight.jpg?v=6',
      showcaseTitle: '<strong>GLPLUS+</strong> 10-IN-1',
      showcaseSub: '10-in-1 Metabolic Signaling Complex',
      showcasePrice: '$39.99 • 60 Capsules',
      showcaseLink: '/products/glp-1-support-capsules',
      badge: 'Metabolic'
    },
    detox: {
      title: 'ZEOLITE+ 8-IN-1',
      subtitle: 'DAILY GUT CLEANSE, DEBLOAT & DIGESTIVE COMFORT',
      points: [
        'Naturally traps and flushes everyday toxins and impurities from the gut',
        'Helps calm occasional bloating, gas, and heaviness after meals',
        'Gentle enough for daily use without stripping healthy minerals'
      ],
      actives: [
        { val: '1,000mg', label: 'Activated Zeolite Complex' },
        { val: 'Purified', label: 'Clinoptilolite & Charcoal' },
        { val: 'Liver', label: 'Milk Thistle & NAC' },
        { val: 'Cleanse', label: 'Psyllium, Aloe & Cascara' }
      ],
      when: ['FIRST THING IN MORNING', 'ON EMPTY STOMACH', 'WITH FULL GLASS WATER'],
      img: 'cdn/shop/files/editorial/cadence-fn-gut.jpg?v=6',
      showcaseTitle: '<strong>ZEOLITE+</strong> 8-IN-1',
      showcaseSub: '8-in-1 Daily Volcanic Cleansing Complex',
      showcasePrice: '$27.99 • 60 Capsules',
      showcaseLink: '/products/zeolite-detox-complex',
      badge: 'Cleanse'
    },
    vitality: {
      title: 'MAGPLUS+ 14-IN-1',
      subtitle: 'ALL-DAY NATURAL ENERGY, MUSCLE RECOVERY & FOCUS',
      points: [
        'Combines 13 gentle, easy-to-absorb forms of magnesium plus phosphatidylserine in one daily formula',
        'Supports steady daily energy, faster muscle recovery, and mental clarity',
        'Easy on your stomach with zero digestive discomfort'
      ],
      actives: [
        { val: '900mg', label: '14-in-1 Mag Complex' },
        { val: '350mg', label: 'Malate, Glycinate & Threonate' },
        { val: '550mg', label: 'Liposomal Mag Total Complex' },
        { val: 'Cellular', label: 'Phosphatidylserine' }
      ],
      when: ['MORNING OR POST-WORKOUT', 'WITH 8OZ WATER', 'DAILY BASELINE'],
      img: 'cdn/shop/files/editorial/cadence-fn-vitality.jpg?v=6',
      showcaseTitle: '<strong>MAGPLUS+</strong> 14-IN-1',
      showcaseSub: '14-in-1 Liposomal Magnesium Complex',
      showcasePrice: '$39.99 • 90 Capsules',
      showcaseLink: '/products/totalmag-13-in-1-magnesium-complex',
      badge: 'Performance'
    }
  };

  function setFunction(key, anchorEl) {
    const data = FUNCTION_DATA[key];
    if (!data) return;

    const prevTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;

    // Update active tab buttons
    document.querySelectorAll('.function-pill').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.func === key);
    });

    // Update in-place accordion cards (expand in place, never jump to top)
    document.querySelectorAll('.function-accordion-card').forEach((card) => {
      const isMatch = card.dataset.func === key;
      card.classList.toggle('open', isMatch);
      const body = card.querySelector('.function-acc-body');
      const icon = card.querySelector('.function-acc-toggle-icon');
      if (body) body.style.display = isMatch ? 'block' : 'none';
      if (icon) icon.textContent = isMatch ? '−' : '+';
    });

    // Update legacy single-card selectors if present
    const cardTitle = document.querySelector('.js-func-active-title');
    const cardSub = document.querySelector('.js-func-active-sub');
    const pointsList = document.querySelector('.js-func-points-list');
    const activesGrid = document.querySelector('.js-func-actives-grid');
    const whenPills = document.querySelector('.js-func-when-pills');

    if (cardTitle) {
      cardTitle.innerHTML = `<span class="ppu-square-bullet"></span> ${data.title}`;
    }
    if (cardSub) cardSub.textContent = data.subtitle;

    if (pointsList) {
      pointsList.innerHTML = data.points
        .map((p, idx) => `
          <div class="function-point-row">
            <span class="point-num-circle">${['①','②','③'][idx] || '•'}</span>
            <span>${p}</span>
          </div>
        `)
        .join('');
    }

    if (activesGrid) {
      activesGrid.innerHTML = data.actives
        .map((a) => `
          <div class="active-stat-col">
            <div class="active-stat-value">${a.val}</div>
            <div class="active-stat-label">${a.label}</div>
          </div>
        `)
        .join('');
    }

    if (whenPills) {
      whenPills.innerHTML = data.when
        .map((w) => `<span class="when-pill">${w}</span>`)
        .join('');
    }

    // Update right showcase
    const showcaseImg = document.querySelector('.js-func-showcase-img');
    const showcaseTitle = document.querySelector('.js-func-showcase-title');
    const showcaseSub = document.querySelector('.js-func-showcase-sub');
    const showcasePrice = document.querySelector('.js-func-showcase-price');
    const badgeTag = document.querySelector('.js-func-badge-tag');

    if (showcaseImg) {
      showcaseImg.style.opacity = '0.3';
      const resolvedImg = (window.PPU_ASSET_MAP && window.PPU_ASSET_MAP[data.img])
        ? window.PPU_ASSET_MAP[data.img]
        : (showcaseImg.src.includes('/products/') ? '../' + data.img : data.img);
      showcaseImg.src = resolvedImg;
      showcaseImg.onload = () => { showcaseImg.style.opacity = '1'; };
    }
    if (showcaseTitle) showcaseTitle.innerHTML = data.showcaseTitle;
    if (showcaseSub) showcaseSub.textContent = data.showcaseSub;
    if (showcasePrice) showcasePrice.textContent = data.showcasePrice;
    document.querySelectorAll('.js-func-showcase-link').forEach((linkEl) => {
      const currentHref = linkEl.getAttribute('href') || '';
      linkEl.href = currentHref.startsWith('../') ? '../' + data.showcaseLink : data.showcaseLink;
    });
    if (badgeTag) badgeTag.textContent = data.badge;

    // Anchor scroll position so the tapped accordion header stays right under the user's thumb
    if (anchorEl && prevTop !== null) {
      const newTop = anchorEl.getBoundingClientRect().top;
      const delta = newTop - prevTop;
      if (Math.abs(delta) > 2) {
        window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
      }
    }
  }

  function initFunctionTabs() {
    document.querySelectorAll('.function-pill').forEach((btn) => {
      btn.addEventListener('click', () => {
        setFunction(btn.dataset.func, null);
      });
    });

    document.querySelectorAll('.function-acc-header').forEach((header) => {
      header.addEventListener('click', (e) => {
        if (e.target.closest('a')) return;
        const card = header.closest('.function-accordion-card');
        if (!card) return;
        const key = card.dataset.func;
        const isAlreadyOpen = card.classList.contains('open');
        if (isAlreadyOpen) {
          card.classList.remove('open');
          const body = card.querySelector('.function-acc-body');
          const icon = card.querySelector('.function-acc-toggle-icon');
          if (body) body.style.display = 'none';
          if (icon) icon.textContent = '+';
        } else {
          setFunction(key, header);
        }
      });
    });

    document.querySelectorAll('.function-collapsed-bar').forEach((bar) => {
      bar.addEventListener('click', () => {
        setFunction(bar.dataset.func, bar);
      });
    });

    const fnKeys = ['sleep', 'weight', 'detox', 'vitality'];
    const prevFnBtn = document.querySelector('.js-func-prev');
    const nextFnBtn = document.querySelector('.js-func-next');
    if (prevFnBtn) {
      prevFnBtn.addEventListener('click', () => {
        const activeBtn = document.querySelector('.function-pill.active');
        const curIdx = Math.max(0, fnKeys.indexOf(activeBtn ? activeBtn.dataset.func : 'sleep'));
        setFunction(fnKeys[(curIdx - 1 + fnKeys.length) % fnKeys.length], null);
      });
    }
    if (nextFnBtn) {
      nextFnBtn.addEventListener('click', () => {
        const activeBtn = document.querySelector('.function-pill.active');
        const curIdx = Math.max(0, fnKeys.indexOf(activeBtn ? activeBtn.dataset.func : 'sleep'));
        setFunction(fnKeys[(curIdx + 1) % fnKeys.length], null);
      });
    }
  }

  // =========================================================================
  // 4. PERFORMANCE BANNER GLASS ACCORDIONS
  // =========================================================================
  function initBannerAccordions() {
    const cards = document.querySelectorAll('.banner-glass-card');
    cards.forEach((card) => {
      const header = card.querySelector('.banner-glass-header');
      if (!header) return;
      header.addEventListener('click', () => {
        const prevTop = header.getBoundingClientRect().top;
        const isOpen = card.classList.contains('open');
        cards.forEach((c) => {
          c.classList.remove('open');
          const body = c.querySelector('.banner-glass-body');
          const icon = c.querySelector('.glass-accordion-icon');
          if (body) body.style.display = 'none';
          if (icon) icon.textContent = '+';
        });
        if (!isOpen) {
          card.classList.add('open');
          const body = card.querySelector('.banner-glass-body');
          const icon = card.querySelector('.glass-accordion-icon');
          if (body) body.style.display = 'block';
          if (icon) icon.textContent = '-';
        }
        const newTop = header.getBoundingClientRect().top;
        const delta = newTop - prevTop;
        if (Math.abs(delta) > 2) {
          window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
        }
      });
    });
  }

  // =========================================================================
  // 5. PDP CONTROLS (Full Carousel + Supplement Facts Quick-Jump + Pack Matrix)
  // =========================================================================
  function initPDP() {
    const thumbs = Array.from(document.querySelectorAll('.pdp-thumb'));
    const mainImg = document.querySelector('.js-pdp-main-img');
    const prevBtn = document.querySelector('.js-pdp-prev');
    const nextBtn = document.querySelector('.js-pdp-next');
    const slideIdxEl = document.querySelector('.js-pdp-slide-idx');
    const jumpFactsBtn = document.querySelector('.js-jump-supplement-facts');

    let currentSlideIdx = 0;

    function goToSlide(idx) {
      if (!thumbs.length || !mainImg) return;
      const total = thumbs.length;
      currentSlideIdx = ((idx % total) + total) % total;

      thumbs.forEach((t, i) => {
        t.classList.toggle('active', i === currentSlideIdx);
      });

      const activeThumb = thumbs[currentSlideIdx];
      const newSrc = activeThumb.getAttribute('data-full-src');
      const newSrcset = activeThumb.getAttribute('data-full-srcset');
      if (newSrc && mainImg.getAttribute('src') !== newSrc) {
        mainImg.style.opacity = '0.35';
        // srcset wins over src, so swap both (the browser picks the size that fits the screen)
        if (newSrcset) {
          mainImg.srcset = newSrcset;
        } else {
          mainImg.removeAttribute('srcset');
        }
        mainImg.src = newSrc;
        mainImg.onload = () => {
          mainImg.style.opacity = '1';
        };
      }
      mainImg.setAttribute('data-current-index', String(currentSlideIdx));

      if (slideIdxEl) {
        slideIdxEl.textContent = String(currentSlideIdx + 1);
      }
    }

    if (thumbs.length && mainImg) {
      thumbs.forEach((thumb, idx) => {
        thumb.addEventListener('click', () => {
          goToSlide(idx);
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          goToSlide(currentSlideIdx - 1);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          goToSlide(currentSlideIdx + 1);
        });
      }

      if (jumpFactsBtn) {
        jumpFactsBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const factsIdx = parseInt(jumpFactsBtn.getAttribute('data-facts-index') || String(thumbs.length - 1), 10);
          goToSlide(factsIdx);
          const stage = document.querySelector('.pdp-stage-box');
          if (stage) {
            stage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      }
    }

    const packOpts = document.querySelectorAll('.pack-card-opt');
    const planRows = document.querySelectorAll('.purchase-plan-row');
    const ctaBtn = document.querySelector('.js-pdp-add-btn');
    const stickyBar = document.getElementById('stickyAtcBar');
    let stickyPriceEl = document.querySelector('.js-sticky-price, .js-sticky-atc-price');
    const stickyPlanEl = document.querySelector('.js-sticky-plan-label, .js-sticky-atc-plan');
    const stickyTriggerBtn = document.querySelector('.js-sticky-atc-trigger');

    let currentQty = 1;
    const defaultPlanRow = document.querySelector('.purchase-plan-row.active');
    let currentPlan = (defaultPlanRow && defaultPlanRow.dataset.planMode) || 'subscribe';

    // Pack badges: Subscribe & Save = 40% (3) / 50% (6); One-Time = 10% (3) / 15% (6)
    const PACK_BADGES = {
      subscribe: { 3: 'Save 40% · Most Popular', 6: 'Save 50% · Best Value' },
      onetime: { 3: 'Save 10% · Most Popular', 6: 'Save 15% · Best Value' }
    };

    function updatePackBadges() {
      const badgeSet = PACK_BADGES[currentPlan] || PACK_BADGES.subscribe;
      packOpts.forEach((opt) => {
        const badge = opt.querySelector('.pack-opt-badge');
        const full = badgeSet[parseInt(opt.dataset.qty, 10)];
        // Phone cards are too narrow for the label at 12px, so show only the saving there
        const text = full && window.innerWidth <= 768 ? full.split(' · ')[0] : full;
        if (badge && text) badge.textContent = text;
      });
    }

    function updatePDP() {
      updatePackBadges();
      if (!ctaBtn) return;
      const basePrice = parseFloat(ctaBtn.dataset.basePrice || '34.99');
      const baseCents = Math.round(basePrice * 100);

      // Shopify One-Time Volume Discount: 1 Bottle = 0% off, 3 Bottles = 10% off, 6 Bottles = 15% off (uses 1-Pack variant x currentQty).
      // Shopify takes the percentage off each bottle and rounds that discount down to the cent ($39.99 less $3.99 = $36.00),
      // so the button matches /cart.js ($108.00 for 3, $204.00 for 6) instead of rounding the discounted price.
      const oneTimePct = currentQty === 3 ? 10 : (currentQty === 6 ? 15 : 0);
      const oneTimeUnitCents = baseCents - Math.floor((baseCents * oneTimePct) / 100);
      const oneTimeTotal = (oneTimeUnitCents * currentQty) / 100;
      const oneTimePerDay = oneTimeTotal / (currentQty * 30);

      // Shopify Subscribe & Save Selling Plans: 1 Bottle = 15% off, 3 Bottles = 40% off, 6 Bottles = 50% off.
      // 3 and 6 bottles = the 1-Pack variant's plan price (data-unit-plan-price, cents, from Liquid) x bottles.
      const activePackOpt = document.querySelector('.pack-card-opt.active');
      const unitPlanCents = activePackOpt ? parseInt(activePackOpt.getAttribute('data-unit-plan-price') || '', 10) : NaN;
      const fallbackPackCents = currentQty === 3 ? Math.floor(baseCents * 0.6) * 3 : Math.floor(baseCents * 0.5) * 6;
      // 1 bottle also reads its plan price from data-unit-plan-price; the typed 15% is only a fallback
      const subTotalCents = unitPlanCents > 0
        ? unitPlanCents * currentQty
        : (currentQty === 1 ? Math.floor(baseCents * 0.85) : fallbackPackCents);
      const subTotal = subTotalCents / 100;
      const subPerDay = subTotal / (currentQty * 30);
      const subSavePct = currentQty === 1 ? 15 : (currentQty === 3 ? 40 : 50);

      const subPriceEl = document.querySelector('.js-sub-price');
      const subPerDayEl = document.querySelector('.js-sub-perday');
      const subTitleEl = document.querySelector('.js-sub-label-title');
      const onePriceEl = document.querySelector('.js-onetime-price');
      const onePerDayEl = document.querySelector('.js-onetime-perday');

      if (subPriceEl) subPriceEl.textContent = `$${subTotal.toFixed(2)}`;
      if (subPerDayEl) subPerDayEl.textContent = `$${subPerDay.toFixed(2)} / day`;
      if (subTitleEl) subTitleEl.textContent = `Subscribe & Save ${subSavePct}%`;
      if (onePriceEl) onePriceEl.textContent = `$${oneTimeTotal.toFixed(2)}`;
      if (onePerDayEl) onePerDayEl.textContent = `$${oneTimePerDay.toFixed(2)} / day`;

      const packVariantId = activePackOpt
        ? (activePackOpt.getAttribute('data-variant-id') || ctaBtn.dataset.variantId1 || '')
        : (ctaBtn.dataset.variantId1 || '');
      const selectedSellingPlan = activePackOpt
        ? (activePackOpt.getAttribute('data-selling-plan') || '692667580787')
        : '692667580787';

      const finalPrice = currentPlan === 'subscribe' ? subTotal : oneTimeTotal;
      // Plan label: the selling plan's own name when the card carries it (data-plan-name, from Liquid)
      const planNameAttr = activePackOpt ? (activePackOpt.getAttribute('data-plan-name') || '').trim() : '';
      const bottlesLabel = `${currentQty} Bottle${currentQty > 1 ? 's' : ''}`;
      const planName = currentPlan === 'subscribe'
        ? (planNameAttr ? `${bottlesLabel} · ${planNameAttr}` : `${bottlesLabel} · Subscribe & Save (Every ${currentQty * 30} days)`)
        : `${bottlesLabel} · One-Time Purchase`;

      // Both plans add the 1-Pack variant x bottles. One-Time 3/6: Shopify applies the automatic 10%/15% discount.
      // Subscribe: the active card's selling plan (3-Pack / 6-Pack plans also apply to the 1-Pack). Never the pack variant.
      ctaBtn.dataset.variantId = currentPlan === 'subscribe' ? (ctaBtn.dataset.variantId1 || '') : (ctaBtn.dataset.variantId1 || packVariantId);
      ctaBtn.dataset.sellingPlan = currentPlan === 'subscribe' ? selectedSellingPlan : '';
      ctaBtn.dataset.productPrice = finalPrice.toFixed(2);
      ctaBtn.dataset.productPlan = planName;
      ctaBtn.dataset.productQty = String(currentQty);
      ctaBtn.dataset.packBottles = String(currentQty);

      // Sold out: the whole product (data-sold-out from Liquid product.available) or the variant this choice would add
      const isSoldOut = ctaBtn.dataset.soldOut === 'true' || isVariantSoldOut(ctaBtn.dataset.variantId);
      ctaBtn.disabled = isSoldOut;
      ctaBtn.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');
      ctaBtn.style.opacity = isSoldOut ? '0.55' : '';
      ctaBtn.style.cursor = isSoldOut ? 'not-allowed' : '';
      ctaBtn.textContent = isSoldOut ? 'SOLD OUT' : `ADD TO PROTOCOL · $${finalPrice.toFixed(2)}`;

      if (stickyTriggerBtn) {
        if (isSoldOut) {
          if (!stickyTriggerBtn.dataset.readyHtml) stickyTriggerBtn.dataset.readyHtml = stickyTriggerBtn.innerHTML;
          stickyTriggerBtn.textContent = 'SOLD OUT';
        } else if (stickyTriggerBtn.dataset.readyHtml) {
          stickyTriggerBtn.innerHTML = stickyTriggerBtn.dataset.readyHtml;
          delete stickyTriggerBtn.dataset.readyHtml;
          stickyPriceEl = document.querySelector('.js-sticky-price, .js-sticky-atc-price');
        }
        stickyTriggerBtn.disabled = isSoldOut;
        stickyTriggerBtn.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');
        stickyTriggerBtn.style.opacity = isSoldOut ? '0.55' : '';
        stickyTriggerBtn.style.cursor = isSoldOut ? 'not-allowed' : '';
      }

      if (stickyPriceEl && !isSoldOut) stickyPriceEl.textContent = `$${finalPrice.toFixed(2)}`;
      if (stickyPlanEl) stickyPlanEl.textContent = isSoldOut ? `${planName} · Sold out` : `${planName} · $${finalPrice.toFixed(2)}`;
    }

    // Pack cards and plan rows act as radio buttons: Tab reaches the chosen one, arrows move, Enter/Space picks
    function syncRadioGroup(group) {
      group.forEach((el) => {
        const on = el.classList.contains('active');
        el.setAttribute('aria-checked', on ? 'true' : 'false');
        el.tabIndex = on ? 0 : -1;
      });
    }
    function makeRadioGroup(group, label) {
      if (!group.length) return;
      const parent = group[0].parentElement;
      if (parent && !parent.getAttribute('role')) {
        parent.setAttribute('role', 'radiogroup');
        parent.setAttribute('aria-label', label);
      }
      const list = Array.from(group);
      list.forEach((el, i) => {
        el.setAttribute('role', 'radio');
        el.addEventListener('keydown', (e) => {
          let next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = list[(i + 1) % list.length];
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = list[(i - 1 + list.length) % list.length];
          if (next) {
            e.preventDefault();
            next.click();
            next.focus();
          } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            el.click();
          }
        });
      });
      syncRadioGroup(group);
    }

    if (packOpts.length) {
      packOpts.forEach((opt) => {
        opt.addEventListener('click', () => {
          packOpts.forEach((o) => o.classList.remove('active'));
          opt.classList.add('active');
          currentQty = parseInt(opt.dataset.qty, 10) || 1;
          syncRadioGroup(packOpts);
          updatePDP();
        });
      });
      makeRadioGroup(packOpts, 'Number of bottles');
    }

    if (planRows.length) {
      planRows.forEach((row) => {
        row.addEventListener('click', () => {
          planRows.forEach((r) => r.classList.remove('active'));
          row.classList.add('active');
          currentPlan = row.dataset.planMode || 'subscribe';
          syncRadioGroup(planRows);
          updatePDP();
        });
      });
      makeRadioGroup(planRows, 'Purchase option');
    }

    updatePDP();

    // S-44: WELCOME20 works on one-time bottles only. A shopper with the welcome cookie or a sign-up in this session
    // starts on One-Time Purchase and sees one line saying so (see welcomeSwitchWanted). Runs once per page view: choosing Subscribe & Save afterwards is never undone.
    applyWelcomeOneTime = function () {
      const box = document.querySelector('.pdp-purchase-options-box');
      if (!box || box.dataset.welcomeNote === '1' || !welcomeSwitchWanted()) return;
      box.dataset.welcomeNote = '1';
      const oneTimeRow = box.querySelector('.purchase-plan-row[data-plan-mode="onetime"]');
      if (oneTimeRow && !oneTimeRow.classList.contains('active')) oneTimeRow.click();
      const note = document.createElement('p');
      note.className = 'pdp-welcome-note';
      note.textContent = 'WELCOME20: 20% off one-time bottles at checkout. Not valid on subscriptions.';
      box.insertAdjacentElement('afterend', note);
    };
    applyWelcomeOneTime();

    // Sticky Bottom Add-to-Protocol Bar scroll observer & trigger
    if (stickyBar && ctaBtn) {
      document.body.classList.add('has-sticky-atc');
      if (stickyTriggerBtn) {
        stickyTriggerBtn.addEventListener('click', (e) => {
          e.preventDefault();
          ctaBtn.click();
        });
      }
      // S-29: on a phone the buy box sits 1.7 screens below the gallery, so the sticky add bar (name, price, add)
      // shows from the first screen instead of waiting for the shopper to scroll past the main add button.
      const phoneSticky = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
      const showStickyBar = () => {
        stickyBar.classList.add('visible');
        document.body.classList.add('sticky-atc-visible');
        // The GET 20% OFF pill sits just above the bar (ppu-cadence.css reads this height)
        document.body.style.setProperty('--sticky-atc-h', `${stickyBar.offsetHeight}px`);
      };
      if (phoneSticky && !stickyBar.hasAttribute('data-sold-out')) showStickyBar();
      // S-36: the GET 20% OFF pill (phones and small tablets, ppu-cadence.css) waits until the main add button has
      // scrolled off the top, so it never covers the product name. Checked once at load and on every scroll.
      const updateWelcomePill = (rect) => {
        document.body.classList.toggle('welcome-pill-ready', rect.bottom < 0);
      };
      updateWelcomePill(ctaBtn.getBoundingClientRect());
      window.addEventListener('scroll', () => {
        const rect = ctaBtn.getBoundingClientRect();
        updateWelcomePill(rect);
        if (rect.bottom < 0 || phoneSticky) {
          showStickyBar();
        } else {
          stickyBar.classList.remove('visible');
          document.body.classList.remove('sticky-atc-visible');
        }
      }, { passive: true });
    }

    // Judge.me reviews render in the Apps section below the product (sections/apps.liquid); the old #pdp-reviews
    // block stays hidden by ppu-cadence.css. If no real review content shows up there 6 seconds after the page loads,
    // hide that section and the star badge under the title instead of leaving them empty
    const reviewsSection = document.querySelector('.shopify-section--apps');
    if (reviewsSection) {
      // The star row under the title still links to the old hidden block; send it to the real reviews instead
      if (reviewsSection.id) {
        document.querySelectorAll('a.pdp-review-rating-row').forEach((link) => link.setAttribute('href', `#${reviewsSection.id}`));
      }
      const hideEmptyReviews = () => {
        const hasJudgeMeMarkup = reviewsSection.querySelector('.jdgm-rev, .jdgm-rev-widg, [class*="jdgm-"]');
        // innerText counts only text that is actually shown, so Judge.me's hidden placeholder markup does not count
        if (hasJudgeMeMarkup && reviewsSection.innerText.trim()) return;
        reviewsSection.style.display = 'none';
        document.querySelectorAll('.pdp-review-rating-row').forEach((row) => {
          if (row.querySelector('.jdgm-preview-badge')) row.style.display = 'none';
        });
      };
      const scheduleReviewsCheck = () => setTimeout(hideEmptyReviews, 6000);
      if (document.readyState === 'complete') {
        scheduleReviewsCheck();
      } else {
        window.addEventListener('load', scheduleReviewsCheck, { once: true });
      }
    }

    document.querySelectorAll('.pdp-accordion-header').forEach((header) => {
      const item = header.closest('.pdp-accordion-item');
      header.setAttribute('aria-expanded', item && item.classList.contains('open') ? 'true' : 'false');
      header.addEventListener('click', () => {
        if (item) {
          item.classList.toggle('open');
          header.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false');
        }
      });
    });
    // Gallery thumbnails and accordion headers are divs: give them button behaviour for keyboard users
    document.querySelectorAll('.pdp-thumb, .pdp-accordion-header').forEach((el) => {
      if (el.matches('button, a[href]')) return;
      if (!el.getAttribute('role')) el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex')) el.tabIndex = 0;
      if (el.classList.contains('pdp-thumb') && !el.getAttribute('aria-label') && el.getAttribute('title')) {
        el.setAttribute('aria-label', el.getAttribute('title'));
      }
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          el.click();
        }
      });
    });
  }

  // =========================================================================
  // 6. SUPPLEMENT FACTS MODAL (DYNAMIC PER PRODUCT)
  // =========================================================================
  const PPUFactsModal = {
    init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-open-facts]');
        if (trigger) {
          e.preventDefault();
          this.open(trigger);
          return;
        }
        if (e.target.closest('.js-close-facts') || e.target.id === 'factsOverlay') {
          this.close();
        }
      });
    },
    open(trigger) {
      const modal = document.getElementById('factsOverlay');
      if (!modal) return;

      const titleEl = document.getElementById('factsModalTitle');
      const imgEl = document.getElementById('factsModalImg');
      const activesListEl = document.getElementById('factsModalActivesList');

      // Resolve product title & facts image dynamically from trigger or current PDP DOM
      const pdpTitle = trigger?.getAttribute('data-facts-title')
        || document.querySelector('.pdp-product-title')?.textContent?.trim()
        || 'Plus+Ultra Formula';
      const thumbs = Array.from(document.querySelectorAll('.pdp-thumb'));
      const lastThumbSrc = thumbs.length ? thumbs[thumbs.length - 1].getAttribute('data-full-src') : '';
      const factsSrc = trigger?.getAttribute('data-facts-img') || lastThumbSrc;

      if (titleEl) {
        titleEl.textContent = `${pdpTitle} · Supplement Facts`;
      }
      if (imgEl && factsSrc) {
        imgEl.src = factsSrc;
        imgEl.alt = `${pdpTitle} Supplement Facts Label`;
      }
      if (activesListEl) {
        const firstAccordionContent = document.querySelector('.pdp-accordion-item .pdp-accordion-content');
        activesListEl.innerHTML = firstAccordionContent ? firstAccordionContent.innerHTML : '';
      }

      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    },
    close() {
      const modal = document.getElementById('factsOverlay');
      if (modal) {
        modal.classList.remove('active');
        document.body.style.overflow = '';
      }
    }
  };

  // =========================================================================
  // 7. CATALOG SORTING & HORIZONTAL SCROLL HELPERS
  // =========================================================================
  function initCatalogSort() {
    const sortSelect = document.getElementById('catalogSort');
    const grid = document.getElementById('catalogProductGrid');
    if (!sortSelect || !grid) return;

    sortSelect.addEventListener('change', (e) => {
      const cards = Array.from(grid.querySelectorAll('.cadence-product-card, .bundle-card'));
      const val = e.target.value;

      cards.sort((a, b) => {
        const priceA = parseFloat(a.dataset.priceOnetime || a.querySelector('[data-product-price]')?.dataset.productPrice || '0');
        const priceB = parseFloat(b.dataset.priceOnetime || b.querySelector('[data-product-price]')?.dataset.productPrice || '0');
        const titleA = a.dataset.productTitle || a.querySelector('.product-card-name, .bundle-title')?.textContent || '';
        const titleB = b.dataset.productTitle || b.querySelector('.product-card-name, .bundle-title')?.textContent || '';

        if (val === 'price-low') return priceA - priceB;
        if (val === 'price-high') return priceB - priceA;
        if (val === 'alpha') return titleA.localeCompare(titleB);
        return 0;
      });

      cards.forEach((c) => grid.appendChild(c));
    });
  }

  function initScrollHelpers() {
    const prevBtn = document.querySelector('.js-scroll-range-prev');
    const nextBtn = document.querySelector('.js-scroll-range-next');
    const grid = document.getElementById('exploreProductGrid');

    if (prevBtn && nextBtn && grid) {
      prevBtn.addEventListener('click', () => {
        grid.scrollBy({ left: -320, behavior: 'smooth' });
      });
      nextBtn.addEventListener('click', () => {
        grid.scrollBy({ left: 320, behavior: 'smooth' });
      });
    }

    const ugcPrev = document.querySelector('.js-ugc-prev');
    const ugcNext = document.querySelector('.js-ugc-next');
    const ugcGrid = document.querySelector('.ugc-carousel-grid-6');
    if (ugcPrev && ugcNext && ugcGrid) {
      ugcPrev.addEventListener('click', () => {
        const first = ugcGrid.firstElementChild;
        const last = ugcGrid.lastElementChild;
        if (first && last) ugcGrid.insertBefore(last, first);
      });
      ugcNext.addEventListener('click', () => {
        const first = ugcGrid.firstElementChild;
        if (first) ugcGrid.appendChild(first);
      });
    }

    const STORY_DATA = {
      founder: {
        tag: 'OUR FORMULATION PROMISE',
        title: 'Why Our Formulas Actually Work',
        body: 'Most supplements use cheap, hard-to-absorb ingredients and hide behind "proprietary blends." We do the opposite. Every Plus+Ultra formula uses premium, body-ready ingredients at the full amounts proven to work in real human studies - with every single milligram listed clearly on the label.',
        bullets: [
          'Body-Ready Ingredients Your System Actually Absorbs',
          'Full Research-Backed Amounts - Zero "Fairy Dusting"',
          '100% Transparent Labels With No Hidden Blends or Fillers'
        ]
      },
      advisor: {
        tag: 'INDEPENDENT US LAB TESTING',
        title: 'Third-Party Lab Tested for Purity & Safety',
        body: 'You deserve to know that what is on the label is 100% clean and actually in the bottle. Every Plus+Ultra batch is made in the USA and independently tested three times by accredited third-party labs for purity, potency, and safety - backed by our 90-Day Money-Back Guarantee.',
        bullets: [
          'Step 1: Raw Ingredient Purity & Identity Screening',
          'Step 2: Clean USA Facility Quality & Weight Checks',
          'Step 3: Independent Third-Party Lab Verification on Every Batch'
        ]
      },
      account: {
        tag: 'CUSTOMER ACCOUNT & SUBSCRIPTIONS',
        title: 'Manage Your Plus+Ultra Subscriptions & Orders',
        body: 'Sign in with your order email to track active US orders or update your billing info. Manage or cancel your subscription anytime from your account, or email support@plusplusultra.com.',
        bullets: [
          'Manage or Cancel Your Subscription Anytime',
          'Real-Time USPS / UPS Order Tracking',
          'Direct Support: support@plusplusultra.com (Mon-Fri 9am-6pm EST)'
        ]
      },
      contact: {
        tag: 'CUSTOMER CONCIERGE',
        title: 'Contact Plus+Ultra Support',
        body: 'Our US-based wellness concierge team responds within 1-2 hours Monday through Friday (9am-6pm EST). Email us directly at support@plusplusultra.com with your order number or formula question.',
        bullets: [
          'Email: support@plusplusultra.com',
          'Hours: Mon-Fri 9am-6pm EST (1-2 Hour Response SLA)',
          'Backed by our 90-Day Money-Back Guarantee'
        ]
      },
      privacy: {
        tag: 'PRIVACY POLICY',
        title: 'Customer Data & Privacy Standards',
        body: 'Plus+Ultra respects your privacy. We never sell personal data to third parties and use bank-grade encryption across all checkout and subscription management flows.'
      },
      terms: {
        tag: 'TERMS OF SERVICE',
        title: 'Plus+Ultra Terms & Subscriptions',
        body: 'All Plus+Ultra subscriptions include 15% to 50% savings depending on pack size, free US shipping, and zero fees. Manage or cancel your subscription anytime from your account, or email support@plusplusultra.com.'
      },
      shipping: {
        tag: 'SHIPPING & 90-DAY GUARANTEE',
        title: 'Free US Shipping & 90-Day Guarantee',
        body: 'Most orders ship within 1-2 business days from our US fulfillment center. Standard US delivery takes 2-5 business days after shipment. Eligible purchases have a 90-day money-back guarantee from delivery; see our refund policy for full terms.'
      },
      accessibility: {
        tag: 'ACCESSIBILITY STATEMENT',
        title: 'WCAG 2.1 AA Digital Accessibility',
        body: 'Plus+Ultra is committed to making our clinical nutrition platform accessible to all customers, with full keyboard navigation, screen-reader labels, and high-contrast typography.'
      }
    };

    const storyOverlay = document.getElementById('storyOverlay');
    const storyBody = document.getElementById('storyModalBody');

    function openStoryModal(key) {
      const info = STORY_DATA[key] || STORY_DATA.founder;
      if (storyBody && storyOverlay) {
        const bulletsHtml = (info.bullets && info.bullets.length)
          ? `<div style="background:#F7F7F5; border-radius:8px; padding:14px 16px; margin-bottom:22px; display:flex; flex-direction:column; gap:8px;">
              ${info.bullets.map(b => `<div style="font-size:13px; font-weight:600; color:#131314; display:flex; align-items:center; gap:8px;"><span style="color:#183426; font-weight:800;">✓</span><span>${b}</span></div>`).join('')}
             </div>`
          : '';
        storyBody.innerHTML = `
          <div style="font-size:11px; font-weight:700; letter-spacing:0.08em; color:#183426; margin-bottom:8px;">${info.tag}</div>
          <h3 style="font-size:24px; font-weight:700; color:#131314; margin-bottom:14px;">${info.title}</h3>
          <p style="font-size:14.5px; line-height:1.65; color:#4A4A4F; margin-bottom:18px;">${info.body}</p>
          ${bulletsHtml}
          <a href="${PPUCart.getShopPath()}" style="display:inline-block; background:#131314; color:#FFF; padding:12px 24px; border-radius:6px; font-size:11px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; text-decoration:none;">Explore Clinical Formulas</a>
        `;
        storyOverlay.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    }

    document.querySelectorAll('.js-open-story').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        openStoryModal(el.getAttribute('data-story') || 'founder');
      });
    });

    // Policy and support anchors use their real URLs, including before Shopify globals load.

    // Account trigger: navigate to /account on Shopify, or open Account modal locally
    document.querySelectorAll('.js-account-trigger').forEach((el) => {
      el.addEventListener('click', (e) => {
        if (window.Shopify) return; // allow native navigation to /account (302 to Shopify Customer Portal)
        e.preventDefault();
        openStoryModal('account');
      });
    });

    document.querySelectorAll('.js-close-story').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (storyOverlay) {
          storyOverlay.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    });
    if (storyOverlay) {
      storyOverlay.addEventListener('click', (e) => {
        if (e.target === storyOverlay) {
          storyOverlay.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    }

    // Instant Search Modal (.js-open-search -> #searchOverlay)
    const searchOverlay = document.getElementById('searchOverlay');
    const searchInput = document.getElementById('searchModalInput');
    const searchResults = document.getElementById('searchModalResults');
    const searchForm = document.querySelector('.js-search-modal-form');

    const SEARCH_CATALOG = [
      { title: 'GLPLUS+ 10-IN-1', cat: 'Weight & GLP-1 · 1,500mg GLP-1 Blend + Berberine HCl 95% + 1B CFU Probiotics', price: '39.99', handle: 'glp-1-support-capsules', vid: '52618400203123', img: 'GLPLUS-10-in-1-Activate-bottle-front-isolated.png' },
      { title: 'MAGPLUS+ 7-IN-1 SLEEP', cat: 'Deep Sleep · 1,050mg Liposomal Magnesium Complex + L-Theanine + Apigenin', price: '34.99', handle: 'magplus-7-in-1-sleep-formula', vid: '62705942004083', img: 'MAGPLUS-Sleep-7-in-1-bottle-front-isolated.png' },
      { title: 'MAGPLUS+ 14-IN-1', cat: 'Full Body Support · 900mg Bioavailable + Liposomal Magnesium Complex', price: '39.99', handle: 'totalmag-13-in-1-magnesium-complex', vid: '62311586529651', img: 'MAGPLUS-14-in-1-Complex-bottle-front-isolated-3000.png' },
      { title: 'ZEOLITE+ 8-IN-1', cat: 'Gut Cleanse & Debloat · 1,000mg Zeolite & Charcoal + Milk Thistle & NAC', price: '27.99', handle: 'zeolite-detox-complex', vid: '62311587086707', img: 'ZEOLITE-8-in-1-bottle-front-isolated.png' },
      { title: 'SOURSOP BITTERS+ 16-IN-1', cat: 'Gut & Detox · 3,100mg Blend: Graviola Leaf + Black Cumin Seed', price: '27.99', handle: 'soursop-bitters', vid: '64625388913011', img: 'SOURSOP-16-in-1-bottle-front-isolated.png' },
      { title: 'GLPLUS+ 20-IN-1 SYNBIOTIC', cat: 'Weight & Gut · Prebiotics + 1B CFU Probiotics + Postbiotics + Enzymes', price: '39.99', handle: 'glp-1-synbiotic', vid: '62567728021875', img: 'GLPLUS-20-in-1-Synbiotic-bottle-front-isolated.png' },
      { title: 'GLPLUS+ 8-IN-1 GUMMIES', cat: 'Weight & GLP-1 · Sugar-Free Gummies + Akkermansia 1B TFU', price: '27.99', handle: 'glp-1-gummies', vid: '62311587217779', img: 'GLPLUS-8-in-1-Activate-Gummies-bottle-front-isolated.png' },
      { title: 'SAFFRON+ 9-IN-1', cat: 'Sleep, Mood & Craving Support · Saffron Extract (0.3% Crocin) + Magnesium Glycinate', price: '29.99', handle: 'saffron', vid: '62595442311539', img: 'SAFFRON-9-in-1-bottle-front-isolated.png' },
      { title: 'GLOPLUS+ 11-IN-1', cat: 'Cellular Detox & Skin Glow · 1,150mg Liposomal Glutathione Complex + Vitamin C', price: '27.99', handle: 'glo-liposomal-glutathione', vid: '62311577616755', img: 'GLOPLUS-11-in-1-bottle-front-isolated.png' },
      { title: 'BEEF ORGANS+ 11-IN-1', cat: 'Full Body Support · Grass-Fed Uterus, Liver, Kidney & Heart', price: '19.99', handle: 'beef-organs-for-women', vid: '62595439985011', img: 'BEEF-ORGANS-11-in-1-bottle-front-isolated.png' },
      { title: 'SLIPPERY ELM+ 8-IN-1', cat: 'Women’s Health · Slippery Elm + Probiotic + pH Balance Blend', price: '27.99', handle: 'viva-la-v-feminine-balance-gummies', vid: '62311573422451', img: 'Viva-La-V-8-in-1-pH-bottle-front-isolated.png' },
      { title: 'TESTPLUS+ 8-IN-1', cat: 'Full Body Support · Tongkat Ali + Fadogia + Ajuga Turkestanica', price: '27.99', handle: 'testplus-testosterone-support', vid: '52622665548147', img: 'TESTPLUS-8-in-1-bottle-front-isolated.png' },
      { title: 'SHILAJITPLUS+ 5-IN-1', cat: 'Full Body Support · 800mg Shilajit (80% Fulvic Acid) + Ashwagandha', price: '27.99', handle: 'shilajit-resin-complex', vid: '62564600217971', img: 'SHILAJITPLUS-5-in-1-bottle-front-isolated.png' },
      { title: 'MACAPLUS+ 3-IN-1', cat: 'Full Body Support · Black Maca + Ashwagandha + Fenugreek', price: '27.99', handle: 'maca-complex', vid: '52618406920563', img: 'MACAPLUS-3-in-1-bottle-front-isolated.png' }
    ];

    function resolveProductHref(handle) {
      if (window.Shopify || window.PPU_ASSET_MAP) return `/products/${handle}`;
      if (window.location.pathname.includes('/products/') || window.location.pathname.includes('/collections/') || window.location.pathname.includes('/pages/')) {
        return `../products/${handle}.html`;
      }
      return `products/${handle}.html`;
    }

    function resolveIsolatedImg(imgFile) {
      const key1 = `cdn/shop/files/isolated/${imgFile}`;
      const key2 = `../cdn/shop/files/isolated/${imgFile}`;
      // Small copy first (PPU_ASSET_THUMB_MAP, same keys, the 240 px copies); PPU_ASSET_MAP only as a fallback
      const thumbs = window.PPU_ASSET_THUMB_MAP;
      if (thumbs && (thumbs[key1] || thumbs[key2])) return thumbs[key1] || thumbs[key2];
      if (window.PPU_ASSET_MAP && (window.PPU_ASSET_MAP[key1] || window.PPU_ASSET_MAP[key2])) {
        return window.PPU_ASSET_MAP[key1] || window.PPU_ASSET_MAP[key2];
      }
      if (window.location.pathname.includes('/products/') || window.location.pathname.includes('/collections/') || window.location.pathname.includes('/pages/')) {
        return key2;
      }
      return key1;
    }

    function searchRowsHtml(rows) {
      return rows.map(row => `
          <div style="display:flex; align-items:center; justify-content:space-between; gap:12px; padding:10px 12px; background:#F8F8F6; border:1px solid #EAEAE6; border-radius:8px;">
            <a href="${escapeHtml(row.href)}" style="display:flex; align-items:center; gap:12px; text-decoration:none; color:#131314; flex:1;">
              <img src="${escapeHtml(row.img)}" alt="${escapeHtml(row.title)}" style="width:46px; height:46px; object-fit:contain; background:#FFF; border-radius:6px; padding:3px;">
              <div>
                <div style="font-size:13.5px; font-weight:700; color:#131314;">${escapeHtml(row.title)}</div>
                <div style="font-size:11.5px; color:#666;">${row.cat ? `${escapeHtml(row.cat)} · ` : ''}<strong>$${escapeHtml(row.price)}</strong></div>
              </div>
            </a>
            <a href="${escapeHtml(row.href)}" class="cadence-tab-pill" style="text-decoration:none; font-size:10.5px; padding:6px 12px; white-space:nowrap;">VIEW →</a>
          </div>
        `).join('');
    }

    function noSearchMatchHtml(query) {
      return `<div style="padding:18px; text-align:center; color:#666; font-size:13.5px;">No matching formulas found for "${escapeHtml(query)}". <a href="${PPUCart.getShopPath()}" style="color:#131314; font-weight:700;">View all ${Number(window.PPU_FORMULA_COUNT) > 0 ? `${Number(window.PPU_FORMULA_COUNT)} ` : ''}formulas →</a></div>`;
    }

    function seeAllResultsHtml(query) {
      if (!query || !window.Shopify) return '';
      return `<a href="/search?q=${encodeURIComponent(query)}" style="display:block; text-align:center; padding:8px 12px; font-size:13px; font-weight:700; color:#131314;">See all results for "${escapeHtml(query)}" →</a>`;
    }

    // Local list: shown for an empty search box, in local previews, and if Shopify's search request fails
    function renderLocalSearchResults(query) {
      const q = (query || '').toLowerCase().trim();
      const matches = q
        ? SEARCH_CATALOG.filter(item => item.title.toLowerCase().includes(q) || item.cat.toLowerCase().includes(q) || item.handle.includes(q))
        : SEARCH_CATALOG.slice(0, 6);

      if (!matches.length) {
        searchResults.innerHTML = noSearchMatchHtml(query);
        return;
      }

      searchResults.innerHTML = searchRowsHtml(matches.map(item => ({
        href: resolveProductHref(item.handle),
        img: resolveIsolatedImg(item.img),
        title: item.title,
        cat: item.cat,
        price: item.price
      }))) + seeAllResultsHtml((query || '').trim());
    }

    function sizedSearchImage(url) {
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=92';
    }

    // Instant results come from Shopify's predictive search, the same engine as the store's search page
    let searchTimer = null;
    let searchSeq = 0;
    function renderSearchModalResults(query) {
      if (!searchResults) return;
      clearTimeout(searchTimer);
      const seq = ++searchSeq;
      const q = (query || '').trim();
      if (!q || !window.Shopify) {
        renderLocalSearchResults(q);
        return;
      }
      searchTimer = setTimeout(() => {
        // If Shopify's search does not answer within 3 seconds, the local list shows instead (via the catch below)
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const giveUp = controller ? setTimeout(() => controller.abort(), 3000) : null;
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,tag,vendor,body', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller ? controller.signal : undefined
        })
          .then((res) => {
            if (!res.ok) throw new Error(`Search failed (${res.status})`);
            return res.json();
          })
          .then((data) => {
            if (seq !== searchSeq) return;
            const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
            if (!products.length) {
              searchResults.innerHTML = noSearchMatchHtml(q);
              return;
            }
            searchResults.innerHTML = searchRowsHtml(products.map((product) => {
              const local = SEARCH_CATALOG.find(item => item.handle === product.handle);
              return {
                href: product.url || resolveProductHref(product.handle),
                img: (local ? resolveIsolatedImg(local.img) : '') || sizedSearchImage((product.featured_image && product.featured_image.url) || product.image),
                title: product.title,
                cat: local ? local.cat : '',
                price: product.price
              };
            })) + seeAllResultsHtml(q);
          })
          .catch(() => {
            if (seq === searchSeq) renderLocalSearchResults(q);
          })
          .finally(() => clearTimeout(giveUp));
      }, 200);
    }

    document.querySelectorAll('.js-open-search').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        if (!searchOverlay) return;
        e.preventDefault();
        renderSearchModalResults(searchInput ? searchInput.value : '');
        searchOverlay.classList.add('active');
        document.body.style.overflow = 'hidden';
        setTimeout(() => { if (searchInput) searchInput.focus(); }, 80);
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        renderSearchModalResults(searchInput.value);
      });
    }

    if (searchForm) {
      searchForm.addEventListener('submit', (e) => {
        if (!window.Shopify) {
          e.preventDefault();
          renderSearchModalResults(searchInput ? searchInput.value : '');
        }
      });
    }

    document.querySelectorAll('.js-close-search').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (searchOverlay) {
          searchOverlay.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    });

    if (searchOverlay) {
      searchOverlay.addEventListener('click', (e) => {
        if (e.target === searchOverlay) {
          searchOverlay.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    }

    // Welcome 20% Off Pop-Up & Connected Newsletter Lead Capture
    const welcomeOverlay = document.getElementById('welcomePopupOverlay');
    const isPressRoom = Boolean(document.getElementById('ppu-press-room'));
    const welcomeFormStep = document.getElementById('welcomeFormStep');
    const welcomeSuccessStep = document.getElementById('welcomeSuccessStep');
    const welcomeLeadForm = document.querySelector('.js-welcome-lead-form');
    const footerNewsletterForm = document.querySelector('.js-footer-newsletter-form');
    const WELCOME_DISMISSED_KEY = 'ppu_welcome_popup_seen_v1';
    const LEADS_STORAGE_KEY = 'ppu_newsletter_leads_v1';

    // Shopify's spam check (hCaptcha, about 1 MB) loads the first time focus lands inside a sign-up form, and the
    // welcome pop-up puts the cursor in its email box by itself, so it loaded on every first visit. Both forms carry
    // data-nocaptcha="true", which Shopify's script skips. The first tap, typing or paste in the email box or submit
    // button removes the flag and sends the focus event Shopify listens for, so the form is protected before it submits.
    function enableSpamCheckOnFirstUse(form) {
      if (!form || form.getAttribute('data-nocaptcha') !== 'true') return;
      const triggers = ['pointerdown', 'touchstart', 'input', 'change'];
      const enable = (e) => {
        const target = e.target instanceof Element ? e.target : null;
        if (!target || !target.closest('input[type="email"], input[type="tel"], [type="submit"]')) return;
        triggers.forEach((type) => form.removeEventListener(type, enable, true));
        form.removeAttribute('data-nocaptcha');
        form.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
      };
      triggers.forEach((type) => form.addEventListener(type, enable, { capture: true, passive: true }));
    }
    enableSpamCheckOnFirstUse(welcomeLeadForm);
    enableSpamCheckOnFirstUse(footerNewsletterForm);

    // WELCOME20 auto-apply. Called only after Shopify accepted the sign-up (or in local previews).
    // The shopper's email and phone are not kept in the browser: Shopify already has them, and an
    // old copy saved by earlier versions of this page is deleted on every visit.
    try { localStorage.removeItem(LEADS_STORAGE_KEY); } catch (e) {}
    const SIGNED_UP_KEY = 'ppu_signed_up_v1';
    const KX_SEEN_KEY = 'ppu_kx_landing_v1';
    function saveNewsletterLead() {
      try {
        localStorage.setItem('ppu_active_discount_code', 'WELCOME20');
        // Separate from the code above, which the subscription guard can remove from the bag later.
        localStorage.setItem(SIGNED_UP_KEY, '1');
      } catch (e) {}
      try { sessionStorage.setItem(WELCOME_SESSION_KEY, '1'); } catch (e) {}
      if (applyWelcomeOneTime) applyWelcomeOneTime();
    }

    // A Klaviyo email click lands with ?_kx=... in the address. Only the fact is kept (never the value), for this tab.
    try { if (new URLSearchParams(window.location.search).has('_kx')) sessionStorage.setItem(KX_SEEN_KEY, '1'); } catch (e) {}

    // The shopper already gave us their email: signed up in this browser, arrived through the welcome email's
    // /discount/WELCOME20 link (Shopify keeps it as the discount_code cookie), or clicked a Klaviyo email (_kx).
    // Only the automatic pop-up is skipped; the code is still shown only after a real sign-up (readSignupResult).
    function alreadyRegistered() {
      try { if (localStorage.getItem(SIGNED_UP_KEY) === '1') return true; } catch (e) {}
      try { if (sessionStorage.getItem(KX_SEEN_KEY) === '1') return true; } catch (e) {}
      return carriesWelcomeCode();
    }

    // Fixed tag list per sign-up form. Values edited in the page are ignored, so a sign-up only ever carries these tags.
    const SIGNUP_TAGS = {
      welcome_popup_vA: 'newsletter,welcome20,popup,welcome_popup_vA',
      footer_newsletter_vA: 'newsletter,welcome20,footer_newsletter_vA'
    };
    const SIGNUP_PENDING_KEY = 'ppu_signup_pending_v1';
    const SIGNUP_FAIL_REPORTED_KEY = 'ppu_signup_fail_reported_v1';
    const LIVE_THEME_ID = 196524409203;

    // Sign-up funnel counters for Klaviyo. Once klaviyo.js has loaded, window.klaviyo.push sends the event. Before
    // that, the event waits in window._klOnsite, the queue Klaviyo's own loader snippet uses and klaviyo.js reads on
    // start. window.klaviyo is never created here: a plain array there stopped klaviyo.js starting on that page.
    // Properties never include the email address or any other form value.
    function ppuTrack(name, props) {
      try {
        const data = Object.assign({
          device: window.matchMedia && window.matchMedia('(max-width: 768px)').matches ? 'phone' : 'desktop',
          page: window.location.pathname
        }, props || {});
        const themeId = window.Shopify && window.Shopify.theme && window.Shopify.theme.id;
        if (themeId) data.theme = Number(themeId) === LIVE_THEME_ID ? 'live' : 'dev';
        const call = ['track', name, data];
        const kl = window.klaviyo;
        if (kl && !Array.isArray(kl) && typeof kl.push === 'function') {
          kl.push(call);
        } else {
          window._klOnsite = window._klOnsite || [];
          window._klOnsite.push(call);
        }
        sendToGa4(GA4_EVENT_NAMES[name], data);
      } catch (err) {}
    }

    // The same events go to Google Analytics (GA4 G-KV8CJC4HLN). Shopify's Google channel loads gtag on the main page,
    // so these also count visitors Klaviyo does not know yet. gtag can arrive a moment after page start (the
    // unconfirmed-return event fires early), so it is retried for up to 10 seconds; never sent if gtag never loads.
    const GA4_EVENT_NAMES = {
      'Signup Popup Shown': 'signup_popup_shown',
      'Signup Popup Submitted': 'signup_popup_submitted',
      'Signup Footer Submitted': 'signup_footer_submitted',
      'Signup Failed': 'signup_failed'
    };
    function sendToGa4(gaName, data, tries) {
      if (!gaName) return;
      if (typeof window.gtag === 'function') {
        try { window.gtag('event', gaName, Object.assign({}, data)); } catch (err) {}
        return;
      }
      if ((tries || 0) < 20) setTimeout(() => sendToGa4(gaName, data, (tries || 0) + 1), 500);
    }

    // The browser blocks the post itself when the address is empty or not an email, so no submit event fires.
    // The input's invalid event is the only signal. One count per form per second, in case a script re-checks.
    // Shopify's spam check can re-send the submit event after its token arrives, so a submit counts once per 5 seconds.
    const lastTrackAt = {};
    function ppuTrackOnce(name, source, windowMs, extra) {
      const key = name + '|' + source;
      const now = Date.now();
      if (lastTrackAt[key] && now - lastTrackAt[key] < windowMs) return;
      lastTrackAt[key] = now;
      ppuTrack(name, Object.assign({ source }, extra || {}));
    }
    function trackSignupFailed(source, reason) {
      ppuTrackOnce('Signup Failed', source, 1000, { reason });
    }
    function watchInvalidEmail(form, source) {
      const input = form && form.querySelector('input[type="email"]');
      if (!input) return;
      input.addEventListener('invalid', () => {
        trackSignupFailed(source, input.validity && input.validity.valueMissing ? 'empty' : 'invalid');
      });
    }
    watchInvalidEmail(welcomeLeadForm, 'welcome_popup_vA');
    watchInvalidEmail(footerNewsletterForm, 'footer_newsletter_vA');

    function newsletterTags(form, source) {
      return SIGNUP_TAGS[source] || 'newsletter';
    }

    // The sign-up is the real form post. Shopify's spam check (hCaptcha) adds its token and submits the form itself;
    // the page used to send its own copy without the token, which Shopify refused (400) and the shopper saw an error.
    // Shopify sends the shopper back with ?customer_posted=true, and readSignupResult() shows the code then.
    // Returns true when the browser should go ahead with the post (live store), false in a local preview.
    function handOffSignup(form, source) {
      const tagsInput = form.querySelector('[name="contact[tags]"]');
      if (tagsInput) tagsInput.value = newsletterTags(form, source);
      try {
        sessionStorage.setItem(SIGNUP_PENDING_KEY, source);
        sessionStorage.removeItem(SIGNUP_FAIL_REPORTED_KEY);
      } catch (err) {}
      if (!window.Shopify) return false;
      setSubmitBusy(form, true);
      setTimeout(() => setSubmitBusy(form, false), 15000);
      return true;
    }

    function setSubmitBusy(form, busy) {
      const btn = form.querySelector('[type="submit"]');
      if (!btn) return;
      btn.disabled = busy;
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
      } else {
        btn.removeAttribute('aria-busy');
      }
    }

    // showingSuccess: the pop-up reopens to show the code after a sign-up; that is not counted as a pop-up view.
    function openWelcomeModal(showingSuccess) {
      if (!welcomeOverlay) return;
      if (showingSuccess !== true && !welcomeOverlay.classList.contains('active')) {
        ppuTrack('Signup Popup Shown', { source: 'welcome_popup_vA' });
      }
      welcomeOverlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        const emailInput = document.getElementById('welcomeEmail');
        if (emailInput && welcomeFormStep && welcomeFormStep.style.display !== 'none') {
          emailInput.focus();
        }
      }, 120);
    }

    function closeWelcomeModal() {
      if (!welcomeOverlay) return;
      welcomeOverlay.classList.remove('active');
      document.body.style.overflow = '';
      try {
        sessionStorage.setItem(WELCOME_DISMISSED_KEY, '1');
      } catch (e) {}
    }

    document.querySelectorAll('.js-open-welcome').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openWelcomeModal();
      });
    });

    document.querySelectorAll('.js-close-welcome').forEach((btn) => {
      btn.addEventListener('click', () => {
        closeWelcomeModal();
      });
    });

    if (welcomeOverlay) {
      welcomeOverlay.addEventListener('click', (e) => {
        if (e.target === welcomeOverlay) closeWelcomeModal();
      });

      // Automatically show as soon as visitor lands on site (unless already seen in this tab session, or the
      // shopper already gave us their email: see alreadyRegistered). The GET 20% OFF pill still opens it.
      try {
        const alreadySeen = sessionStorage.getItem(WELCOME_DISMISSED_KEY);
        if (!isPressRoom && !alreadySeen && !alreadyRegistered() && !navigator.webdriver) {
          setTimeout(() => {
            openWelcomeModal();
          }, 500);
        }
      } catch (e) {
        if (!isPressRoom && !alreadyRegistered() && !navigator.webdriver) {
          setTimeout(openWelcomeModal, 500);
        }
      }
    }

    function showSignupSuccess(source) {
      saveNewsletterLead();
      // The code is not printed in the page HTML; it is written into the pop-up's code box only after Shopify
      // confirmed the sign-up (?customer_posted=true). Without this line the box is empty after a sign-up.
      document.querySelectorAll('[data-ppu-code-slot="welcome"]').forEach((slot) => { slot.textContent = 'WELCOME20'; });
      if (source === 'welcome_popup_vA' && welcomeOverlay) {
        if (welcomeFormStep) welcomeFormStep.style.display = 'none';
        if (welcomeSuccessStep) welcomeSuccessStep.style.display = 'block';
        openWelcomeModal(true);
        try { sessionStorage.setItem(WELCOME_DISMISSED_KEY, '1'); } catch (err) {}
        PPUCart.showToast('20% Off Welcome Code (WELCOME20) Unlocked!');
        return;
      }
      const feedback = footerNewsletterForm && footerNewsletterForm.querySelector('.js-footer-newsletter-feedback');
      if (feedback) {
        feedback.style.display = 'block';
        feedback.innerHTML = '✓ Connected! Your 20% off welcome code is <strong>WELCOME20</strong> (applied at checkout). Valid on one-time purchases only.';
        footerNewsletterForm.scrollIntoView({ block: 'center' });
      }
      PPUCart.showToast('Subscribed! Use code WELCOME20 for 20% off.');
    }

    // Only Shopify's own confirmation (?customer_posted=true after the form post) counts as a sign-up, and only
    // when this tab sent a sign-up (the pending flag set on submit). A typed or shared ?customer_posted=true link
    // with no submit behind it shows nothing and does not unlock WELCOME20.
    function readSignupResult() {
      const params = new URLSearchParams(window.location.search);
      let source = '';
      try { source = sessionStorage.getItem(SIGNUP_PENDING_KEY) || ''; } catch (err) {}
      if (params.get('customer_posted') !== 'true') {
        // A sign-up was sent but this page came back without Shopify's confirmation. Shopify's spam-check page
        // (/challenge) is a step on the way, not a failure, so it is skipped. Counted once per submit; the flag stays
        // so a later confirmation still shows the code.
        if (source && !/^\/challenge/.test(window.location.pathname)) {
          let reported = '';
          try { reported = sessionStorage.getItem(SIGNUP_FAIL_REPORTED_KEY) || ''; } catch (err) {}
          if (!reported) {
            try { sessionStorage.setItem(SIGNUP_FAIL_REPORTED_KEY, '1'); } catch (err) {}
            ppuTrack('Signup Failed', { source, reason: 'not_confirmed' });
          }
        }
        return;
      }
      params.delete('customer_posted');
      const query = params.toString();
      try { history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '')); } catch (err) {}
      if (!source) return;
      try {
        sessionStorage.removeItem(SIGNUP_PENDING_KEY);
        sessionStorage.removeItem(SIGNUP_FAIL_REPORTED_KEY);
      } catch (err) {}
      showSignupSuccess(source);
    }

    if (welcomeLeadForm) {
      welcomeLeadForm.addEventListener('submit', (e) => {
        const email = document.getElementById('welcomeEmail')?.value.trim() || '';
        if (!email) {
          e.preventDefault();
          trackSignupFailed('welcome_popup_vA', 'empty');
          return;
        }
        ppuTrackOnce('Signup Popup Submitted', 'welcome_popup_vA', 5000);
        if (!handOffSignup(welcomeLeadForm, 'welcome_popup_vA')) {
          e.preventDefault();
          showSignupSuccess('welcome_popup_vA');
        }
      });
    }

    // Says "copied" only when the clipboard write worked; the code is entered at checkout, never "applied to your bag"
    document.querySelectorAll('.js-copy-welcome-code').forEach((btn) => {
      btn.addEventListener('click', () => {
        const copied = () => {
          btn.textContent = 'COPIED ✓';
          PPUCart.showToast('Code WELCOME20 copied. Enter it at checkout.');
        };
        const failed = () => PPUCart.showToast('Could not copy. Your code is WELCOME20, enter it at checkout.', true);
        try {
          navigator.clipboard.writeText('WELCOME20').then(copied, failed);
        } catch (e) {
          failed();
        }
      });
    });

    if (footerNewsletterForm) {
      footerNewsletterForm.addEventListener('submit', (e) => {
        const emailInput = footerNewsletterForm.querySelector('input[type="email"]');
        if (!emailInput || !emailInput.value.trim()) {
          e.preventDefault();
          trackSignupFailed('footer_newsletter_vA', 'empty');
          return;
        }
        ppuTrackOnce('Signup Footer Submitted', 'footer_newsletter_vA', 5000);
        if (!handOffSignup(footerNewsletterForm, 'footer_newsletter_vA')) {
          e.preventDefault();
          showSignupSuccess('footer_newsletter_vA');
        }
      });
    }

    readSignupResult();
  }

  // =========================================================================
  // 8. 30-SECOND CLINICAL PROTOCOL QUIZ (pages/quiz.html)
  // =========================================================================
  
  // Shopify Quiz Result Adapter
  function renderQuizResult(rec) {
    if (!rec) return rec;
    var copy = Object.assign({}, rec);
    if (copy.img && window.PPU_ASSET_MAP && window.PPU_ASSET_MAP[copy.img]) {
      copy.img = window.PPU_ASSET_MAP[copy.img];
    }
    if (copy.url) {
      copy.url = copy.url.replace('../products/', '/products/').replace('.html', '');
    }
    return copy;
  }
  window.renderQuizResult = renderQuizResult;

  function initQuiz() {
    const quizContainer = document.getElementById('ppuProtocolQuiz');
    if (!quizContainer) return;

    const steps = Array.from(quizContainer.querySelectorAll('.js-quiz-step'));
    const resultView = document.getElementById('quizResultView');
    const progressFill = document.getElementById('quizProgressFill');
    const stepIndicator = document.getElementById('quizStepIndicator');
    const answers = {};
    const answerLabels = {};
    let currentStep = 0;

    function showStep(idx) {
      currentStep = idx;
      steps.forEach((s, i) => {
        s.style.display = i === idx ? 'block' : 'none';
      });
      if (progressFill) {
        progressFill.style.width = `${Math.min(100, ((idx + 1) / steps.length) * 100)}%`;
      }
      if (stepIndicator) {
        stepIndicator.textContent = `STEP ${idx + 1} OF ${steps.length}`;
      }
    }

    quizContainer.querySelectorAll('.js-quiz-opt').forEach((btn) => {
      btn.addEventListener('click', () => {
        const stepIdx = parseInt(btn.getAttribute('data-step') || '0', 10);
        const goalVal = btn.getAttribute('data-value') || 'sleep';
        const optTitle = btn.querySelector('.quiz-option-title')?.textContent?.trim() || goalVal;
        answers[stepIdx] = goalVal;
        answerLabels[stepIdx] = optTitle;

        if (stepIdx + 1 < steps.length) {
          showStep(stepIdx + 1);
        } else {
          steps.forEach((s) => { s.style.display = 'none'; });
          if (progressFill) progressFill.style.width = '100%';
          if (stepIndicator) stepIndicator.textContent = 'PERSONALIZED CLINICAL PROTOCOL READY';
          if (resultView) {
            resultView.style.display = 'block';
            const primaryGoal = answers[0] || 'sleep';
            const summaryEl = document.getElementById('quizPersonalSummary');
            if (summaryEl) {
              summaryEl.textContent = `Matched for: ${answerLabels[0] || 'Daily Wellness'} · ${answerLabels[1] || 'All-Day Support'} · ${answerLabels[2] || 'Subscribe & Save'}`;
              summaryEl.style.display = 'inline-block';
            }
            resultView.querySelectorAll('.js-quiz-rec-panel').forEach((panel) => {
              panel.style.display = panel.getAttribute('data-rec-goal') === primaryGoal ? 'block' : 'none';
            });
          }
        }
      });
    });

    const restartBtn = document.querySelector('.js-quiz-restart');
    if (restartBtn) {
      restartBtn.addEventListener('click', () => {
        if (resultView) resultView.style.display = 'none';
        showStep(0);
      });
    }
  }

  function initHeroVideo() {
    const heroVid = document.getElementById('heroWellnessVideo');
    const heroBtn = document.getElementById('heroVideoToggle');
    const heroIcon = document.getElementById('heroVideoIcon');
    if (heroVid && heroBtn) {
      heroBtn.addEventListener('click', () => {
        if (heroVid.paused) {
          heroVid.play();
          if (heroIcon) heroIcon.textContent = '⏸';
        } else {
          heroVid.pause();
          if (heroIcon) heroIcon.textContent = '▶';
        }
      });
    }
  }

  function initMobileNav() {
    const drawer = document.getElementById('mobileNavDrawer');
    if (!drawer) return;

    document.addEventListener('click', (e) => {
      if (e.target.closest('.mobile-nav-toggle, .js-open-mobile-nav')) {
        e.preventDefault();
        drawer.classList.add('active');
        document.body.style.overflow = 'hidden';
        return;
      }
      if (e.target.closest('.js-close-mobile-nav')) {
        drawer.classList.remove('active');
        document.body.style.overflow = '';
        return;
      }
    });
  }

  // Bag drawer and pop-ups for keyboard users: focus moves in when one opens, Tab stays inside it,
  // Escape closes it, and focus goes back to the button that opened it
  function initDialogKeyboard() {
    const DIALOGS = [
      { id: 'cartDrawer', close: () => PPUCart.close() },
      { id: 'factsOverlay', closeSel: '.js-close-facts', close: () => PPUFactsModal.close() },
      { id: 'searchOverlay', closeSel: '.js-close-search' },
      { id: 'storyOverlay', closeSel: '.js-close-story' },
      { id: 'welcomePopupOverlay', closeSel: '.js-close-welcome' },
      { id: 'accountPortalOverlay', closeSel: '.js-close-account-portal' },
      { id: 'mobileNavDrawer', closeSel: '.js-close-mobile-nav' }
    ].map((d) => Object.assign({ el: document.getElementById(d.id) }, d)).filter((d) => d.el);
    if (!DIALOGS.length) return;
    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const visible = (el) => Boolean(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    const focusables = (el) => Array.from(el.querySelectorAll(FOCUSABLE)).filter(visible);
    const isOpen = (d) => d.el.classList.contains('active');
    const topOpen = () => DIALOGS.filter(isOpen).pop() || null;
    let opener = null;

    DIALOGS.forEach((d) => {
      let wasOpen = isOpen(d);
      new MutationObserver(() => {
        const nowOpen = isOpen(d);
        if (nowOpen === wasOpen) return;
        wasOpen = nowOpen;
        if (nowOpen) {
          if (!d.el.contains(document.activeElement)) opener = document.activeElement;
          setTimeout(() => {
            if (!isOpen(d) || d.el.contains(document.activeElement)) return;
            const first = d.el.querySelector(d.closeSel || '.js-close-cart') || focusables(d.el)[0];
            if (first) first.focus();
            else { if (!d.el.hasAttribute('tabindex')) d.el.tabIndex = -1; d.el.focus(); }
          }, 150);
        } else if (!topOpen() && opener && document.contains(opener)) {
          opener.focus();
          opener = null;
        }
      }).observe(d.el, { attributes: true, attributeFilter: ['class'] });
    });

    document.addEventListener('keydown', (e) => {
      const d = topOpen();
      if (!d) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        const btn = d.closeSel ? d.el.querySelector(d.closeSel) : null;
        if (d.close) d.close();
        else if (btn) btn.click();
        else d.el.classList.remove('active');
        if (isOpen(d)) d.el.classList.remove('active');
        if (!topOpen()) document.body.style.overflow = '';
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables(d.el);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (!d.el.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  // DOM Ready
  document.addEventListener('DOMContentLoaded', () => {
    markSoldOutButtons();
    PPUCart.init();
    initMobileNav();
    initCategoryFilters();
    initFunctionTabs();
    initBannerAccordions();
    initPDP();
    PPUFactsModal.init();
    initCatalogSort();
    initScrollHelpers();
    initQuiz();
    initHeroVideo();
    initDialogKeyboard();
  });

  window.PPUCart = PPUCart;
})();
